/** React Query hooks for shipments API */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance, { apiGet, apiPost, apiPut, apiPatch, apiDelete, apiRequest } from '../../../shared/api/axiosInstance';
import { shipmentsDashboardKeys } from './useDashboard';
import type {
  ShipmentDocumentParseFailureReason,
  ShipmentDocumentParsePartState,
  ShipmentDocumentParsePlanPath,
  ShipmentDocumentParseStatus,
} from '../../../generated/config-enums';
import { SHIPMENT_DOCUMENT_PARSE_PLAN_PATH_CHECK_MISSED_VALUES } from '../../../generated/config-enums';
import type { InboundDeliveryList } from '../../../generated/api/models/inboundDeliveryList';
import type { ShipmentDocumentTriageResolve } from '../../../generated/api/models/shipmentDocumentTriageResolve';
import type { TenantDocumentType } from '../../../generated/api/models/tenantDocumentType';
import type { TenantDocumentTypeCreate } from '../../../generated/api/models/tenantDocumentTypeCreate';
import type { TenantDocumentTypeDeleteResult } from '../../../generated/api/models/tenantDocumentTypeDeleteResult';
import type { TenantDocumentTypeUpdate } from '../../../generated/api/models/tenantDocumentTypeUpdate';
import { shipmentDocIntakeKey } from './useShipmentDocIntake';
import { getShipmentFilterFacetsApiV1ShipmentsFilterFacetsGet } from '../../../generated/api/shipments/shipments';
import { getPortLocationFilterFacetsApiV1ShipmentsReferenceDataPortsLocationsFilterFacetsGet } from '../../../generated/api/shipments-reference-data/shipments-reference-data';
import type { SalesDelivery } from '../../../shared/sales/types';
import type {
  ScreeningResponse,
  ScreeningResult,
  DispositionRequest,
} from '../../../shared/compliance';
import type {
  ShipmentDiversion,
  ShipmentDivertPreview,
  ShipmentDivertRequest,
  ShipmentDivertResult,
} from '../types';
import type {
  ShipmentCoverage,
  ShipmentCoverageDetail,
  ShipmentCoverageUpdate,
  InsuranceCertificate,
  InsuranceCertificateCreate,
  InsuranceCertificateUpdate,
} from '../types';
import type {
  ShipmentProcurementOrder,
  Airline,
  Booking,
  CarrierMessage,
  CarrierMessageDetail,
  CarrierMessageType,
  BookingCreate,
  BookingUpdate,
  BookingCutoff,
  BookingCutoffCreate,
  BookingCutoffUpdate,
  BookingEquipment,
  BookingEquipmentCreate,
  BookingEquipmentUpdate,
  PartnerRequest,
  PartnerRequestCreate,
  PartnerRequestUpdate,
  PartnerRequestRespond,
  FreightQuoteRequest,
  FreightQuoteRequestCreate,
  FreightQuoteRequestUpdate,
  FreightQuoteRecipientInput,
  FreightQuoteRecord,
  FreightQuoteApprovalRequest,
  FreightQuoteDecision,
  ChecklistTemplatesResponse,
  ListChecklistTemplatesParams,
  ShipmentChecklist,
  LoadUnitType,
  HsCode,
  ListHsCodesParams,
  OceanCarrier,
  PortLocation,
  PortSuggestion,
  RoutingApplyResponse,
  RoutingEligibility,
  RoutingPlanResponse,
  Shipment,
  ShipmentAssignee,
  ShipmentAssigneeCreate,
  ShipmentCargo,
  ShipmentCargoCreate,
  ShipmentCargoUpdate,
  ShipmentCargoContentsLink,
  ShipmentCargoContentsLinkCreate,
  ShipmentCargoContentsLinkUpdate,
  ShipmentCargoEnumerate,
  ShipmentCharge,
  ShipmentChargeCreate,
  ShipmentChargeUpdate,
  ShipmentChargeList,
  ChargeCode,
  ChargeCodeCreate,
  ChargeCodeUpdate,
  ListChargeCodesParams,
  ContainerDndClock,
  ExceptionQueueRow,
  ExceptionGroupBy,
  ExceptionGroupSummary,
  ExceptionAgingSummary,
  UninvoicedWipResponse,
  DndDispute,
  DndDisputeCreate,
  DndDisputeUpdate,
  DgDeclaration,
  DgDeclarationCreate,
  DgDeclarationLine,
  DgDeclarationLineCreate,
  DgDeclarationLineUpdate,
  DgDeclarationSign,
  DgDeclarationUpdate,
  ExportControlRecord,
  ExportControlRecordCreate,
  ExportControlRecordUpdate,
  FreetimeTerm,
  FreetimeTermCreate,
  FreetimeTermUpdate,
  HolidayCalendar,
  HolidayCalendarCreate,
  HolidayCalendarUpdate,
  WarehouseIntakeCapacity,
  WarehouseIntakeCapacityCreate,
  WarehouseIntakeCapacityUpdate,
  TerminalAppointment,
  TerminalAppointmentCreate,
  TerminalAppointmentUpdate,
  ChassisUsage,
  ChassisUsageCreate,
  ChassisUsageUpdate,
  StreetTurn,
  StreetTurnCreate,
  StreetTurnUpdate,
  ShipmentCreate,
  ShipmentDocument,
  ShipmentDocumentCreate,
  ShipmentDocumentUpdate,
  ShipmentDocumentTransition,
  ShipmentDocumentReissue,
  BLReleaseState,
  DocumentReleaseTransition,
  ShipmentException,
  ShipmentExceptionCreate,
  ShipmentLeg,
  ShipmentLegCreate,
  ShipmentLegUpdate,
  ShipmentLineItem,
  ShipmentLineItemCreate,
  ShipmentLineItemUpdate,
  ShipmentImportResult,
  ShipmentListResponse,
  ShipmentMilestone,
  ShipmentMilestoneCreate,
  ShipmentParty,
  ShipmentPartyCreate,
  ShipmentReference,
  ShipmentReferenceCreate,
  ShipmentBillRow,
  ShipmentCandidateBill,
  ShipmentBillAttachRequest,
  ShipmentBillAttachResult,
  ShipmentInvoiceRow,
  ShipmentCandidateInvoice,
  ShipmentInvoiceAttachRequest,
  ShipmentInvoiceAttachResult,
  InvoiceableChargesResponse,
  InvoiceFromChargesRequest,
  InvoiceFromChargesResult,
  BillableChargesResponse,
  BillFromChargesRequest,
  BillFromChargesResult,
  ShipmentCostRollup,
  ShipmentCostAllocation,
  ShipmentCostSummary,
  ShipmentEstimateReminder,
  JobPnl,
  JobCloseState,
  FreightAccrualPlan,
  FreightAccrualRunResult,
  FreightAccrualRunRequest,
  ShipmentService,
  ShipmentServiceCreate,
  ShipmentUpdate,
  TrackingCapabilities,
  TrackingSubscription,
  TrackingSubscriptionCreate,
  SuggestPortsParams,
  ListResponse,
  ListAirlinesParams,
  ListOceanCarriersParams,
  ListPortsLocationsParams,
  ListLoadUnitTypesParams,
  ListShipmentsParams,
} from '../types';

// How often the document drop panel re-reads its parse jobs while any is still
// running. Matches the ingestion review table's cadence, so the two intake
// surfaces feel equally responsive.
const POLL_INTERVAL_MS = 2000;

const SHIPMENTS_API = '/api/v1/shipments';
const REF_DATA_API = `${SHIPMENTS_API}/reference-data`;
const ROUTING_API = `${SHIPMENTS_API}/routing`;

// Query keys
export const shipmentsKeys = {
  all: ['shipments'] as const,
  list: (params?: ListShipmentsParams) => ['shipments', 'list', params] as const,
  filterFacets: (adminScope?: string, activeCompanyId?: string | null) =>
    ['shipments', 'filter-facets', adminScope, activeCompanyId] as const,
  detail: (id: string) => ['shipments', 'detail', id] as const,
  legs: (shipmentId: string) => ['shipments', 'legs', shipmentId] as const,
  assignees: (shipmentId: string) => ['shipments', 'assignees', shipmentId] as const,
  parties: (shipmentId: string) => ['shipments', 'parties', shipmentId] as const,
  services: (shipmentId: string) => ['shipments', 'services', shipmentId] as const,
  cargo: (shipmentId: string) => ['shipments', 'cargo', shipmentId] as const,
  cargoContents: (shipmentId: string) => ['shipments', 'cargo-contents', shipmentId] as const,
  lineItems: (shipmentId: string) => ['shipments', 'line-items', shipmentId] as const,
  references: (shipmentId: string) => ['shipments', 'references', shipmentId] as const,
  documents: (shipmentId: string) => ['shipments', 'documents', shipmentId] as const,
  checklist: (shipmentId: string) => ['shipments', 'checklist', shipmentId] as const,
  tenantDocumentTypes: (includeInactive = false) =>
    ['shipments', 'tenantDocumentTypes', includeInactive] as const,
  deliveryReleaseStatus: (shipmentId: string) =>
    ['shipments', 'delivery-release-status', shipmentId] as const,
  blRelease: (shipmentId: string) => ['shipments', 'bl-release', shipmentId] as const,
  documentRelease: (shipmentId: string, documentId: string) =>
    ['shipments', 'document-release', shipmentId, documentId] as const,
  checklistTemplates: (params?: ListChecklistTemplatesParams) =>
    ['shipments', 'checklist-templates', params] as const,
  charges: (shipmentId: string) => ['shipments', 'charges', shipmentId] as const,
  coverage: (shipmentId: string) => ['shipments', 'coverage', shipmentId] as const,
  chargeCodes: (params?: ListChargeCodesParams) => ['shipments', 'charge-codes', params] as const,
  milestones: (shipmentId: string) => ['shipments', 'milestones', shipmentId] as const,
  exceptions: (shipmentId: string) => ['shipments', 'exceptions', shipmentId] as const,
  diversions: (shipmentId: string) => ['shipments', 'diversions', shipmentId] as const,
  screening: (shipmentId: string) => ['shipments', 'screening', shipmentId] as const,
  trackingSubscriptions: (shipmentId: string) => ['shipments', 'tracking', shipmentId] as const,
  trackingCapabilities: () => ['shipments', 'tracking', 'capabilities'] as const,
  bookings: (shipmentId: string) => ['shipments', 'bookings', shipmentId] as const,
  carrierMessages: (shipmentId: string, bookingId: string) =>
    ['shipments', 'carrier-messages', shipmentId, bookingId] as const,
  carrierMessage: (shipmentId: string, bookingId: string, messageId: string) =>
    ['shipments', 'carrier-messages', shipmentId, bookingId, messageId] as const,
  partnerRequests: (shipmentId: string) => ['shipments', 'partner-requests', shipmentId] as const,
  freightQuoteRequests: (shipmentId: string) => ['shipments', 'freight-quote-requests', shipmentId] as const,
  loadUnitTypes: (params?: ListLoadUnitTypesParams) => ['shipments', 'load-unit-types', params] as const,
  portsLocations: (params?: ListPortsLocationsParams) => ['shipments', 'ports-locations', params] as const,
  portLocationFilterFacets: () => ['shipments', 'ports-locations', 'filter-facets'] as const,
  airlines: (params?: ListAirlinesParams) => ['shipments', 'airlines', params] as const,
  oceanCarriers: (params?: ListOceanCarriersParams) => ['shipments', 'ocean-carriers', params] as const,
  hsCodes: (params?: ListHsCodesParams) => ['shipments', 'hs-codes', params] as const,
  hsCodeNomenclatures: () => ['shipments', 'hs-code-nomenclatures'] as const,
  routingEligibility: (shipmentId: string) => ['shipments', 'routing-eligibility', shipmentId] as const,
  routingSuggestPorts: (params?: SuggestPortsParams) => ['shipments', 'routing-suggest-ports', params] as const,
  terminalAppointments: (legId: string) => ['shipments', 'terminal-appointments', legId] as const,
  chassisUsage: (legId: string) => ['shipments', 'chassis-usage', legId] as const,
  shipmentStreetTurns: (shipmentId: string) => ['shipments', 'street-turns', shipmentId] as const,
};

// ─── Shipment CRUD ───

function buildQueryString(params: object): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === '') continue;
    // A multi-value filter is a REPEATABLE param (?status=a&status=b), which is
    // what the `list[str] = Query(...)` routes parse. String(array) would send
    // one comma-joined value that arrives as the single element "a,b" and
    // matches nothing — a filter that silently returns an empty list.
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v != null && v !== '') sp.append(key, String(v));
      });
      continue;
    }
    sp.set(key, String(value));
  }
  const q = sp.toString();
  return q ? `?${q}` : '';
}

export function useShipments(
  params?: ListShipmentsParams,
  /** `enabled: false` holds the request — for cross-module callers that only
   *  want the count once a prerequisite id resolves, or that must not call
   *  shipments at all for a user without the module (VendorDetailPage). */
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: shipmentsKeys.list(params),
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: () => apiGet<ShipmentListResponse>(`${SHIPMENTS_API}${buildQueryString(params ?? {})}`),
    enabled: options?.enabled ?? true,
  });
}

export function useShipmentFilterFacets(adminScope?: string, activeCompanyId?: string | null) {
  return useQuery({
    // The active-company value travels in the axios header, but it must still
    // participate in the cache identity so switching companies cannot reuse a
    // sibling company's facet candidates.
    queryKey: shipmentsKeys.filterFacets(adminScope, activeCompanyId),
    queryFn: ({ signal }) =>
      getShipmentFilterFacetsApiV1ShipmentsFilterFacetsGet(
        adminScope ? { admin_scope: adminScope } : undefined,
        signal,
      ),
  });
}

export function useShipment(id: string) {
  return useQuery({
    queryKey: shipmentsKeys.detail(id),
    queryFn: () => apiGet<Shipment>(`${SHIPMENTS_API}/${id}`),
    enabled: !!id,
  });
}

// Contract G — the sales deliveries consolidated onto this transport shipment.
// The link is one-directional (FK on the Sales side), so we discover them
// through the Sales API rather than a reverse relationship here.
export function useLinkedFulfilmentDocs(shipmentId: string) {
  return useQuery({
    queryKey: ['shipments', 'fulfilment-docs', shipmentId],
    queryFn: () =>
      apiGet<{ items: SalesDelivery[] }>(
        `/api/v1/sales/deliveries/by-transport/${shipmentId}`,
      ),
    enabled: !!shipmentId,
  });
}

// Contract H — the procurement PO↔shipment intake documents linked to this
// transport shipment. Like Contract G, the link is one-directional (FK on the
// procurement side), so we discover them through the procurement API.
export function useLinkedProcurementOrders(shipmentId: string) {
  return useQuery({
    queryKey: ['shipments', 'procurement-orders', shipmentId],
    queryFn: () =>
      apiGet<InboundDeliveryList>(
        `/api/v1/procurement/inbound-deliveries?transport_shipment_id=${shipmentId}`,
      ),
    enabled: !!shipmentId,
  });
}

const INBOUND_DELIVERY_API = '/api/v1/procurement/inbound-deliveries';

// Best-effort: after a PO/SO link change the shipment's derived commerce
// direction may have changed, so ask the backend to re-pick the checklist
// template (e.g. an inbound import upgrades to "Ocean Import"), then refresh the
// checklist views. Secondary to the link mutation — a re-eval hiccup is
// swallowed so it never surfaces as a failed link.
async function reevaluateChecklist(
  shipmentId: string,
  qc: ReturnType<typeof useQueryClient>,
): Promise<void> {
  try {
    await apiPost(`${SHIPMENTS_API}/${shipmentId}/checklist/reevaluate`, {});
  } catch {
    return;
  }
  qc.invalidateQueries({ queryKey: shipmentsKeys.checklist(shipmentId) });
  qc.invalidateQueries({ queryKey: shipmentsKeys.documents(shipmentId) });
  qc.invalidateQueries({ queryKey: shipmentsKeys.milestones(shipmentId) });
}

/** Minimal PO shape the Orders-tab picker needs. Read over procurement's REST
 * surface (never by importing procurement code — frontend module boundary). */
export interface LinkablePurchaseOrder {
  id: string;
  po_number: string;
  vendor_name: string | null;
  status: string;
  expected_delivery_date: string | null;
  /** Owning legal entity — shown in the picker to disambiguate duplicate PO
   * numbers across companies when more than one company is in scope. */
  company_id: string | null;
  company_name: string | null;
}

// Eligible POs to link to a shipment. The procurement list endpoint returns
// `{ data: [...] }`; we surface the array for the picker. The picker filters
// client-side, so we request the endpoint max (1000) rather than leaving the
// limit unbounded — a server-side typeahead is the follow-up for huge tenants.
export function useLinkablePurchaseOrders() {
  return useQuery({
    queryKey: ['shipments', 'linkable-purchase-orders'],
    queryFn: () => apiGet<{ data: LinkablePurchaseOrder[] }>('/api/v1/procurement/orders?limit=1000'),
    select: (resp) => resp.data,
  });
}

// Contract H write-path — link one or more existing POs to this transport
// shipment in a single pass (consolidation use case). The junction is owned by
// procurement and its endpoint takes one PO per call, so we fan out the POSTs
// and invalidate the shipment-side discovery query once they all settle.
export function useLinkPurchaseOrdersToShipment(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (purchaseOrderIds: string[]) =>
      Promise.all(
        purchaseOrderIds.map((purchaseOrderId) =>
          apiPost<{ data: ShipmentProcurementOrder }>(INBOUND_DELIVERY_API, {
            purchase_order_id: purchaseOrderId,
            transport_shipment_id: shipmentId,
          }),
        ),
      ),
    // Invalidate on settled rather than success: the fan-out can partially
    // succeed (one PO POST rejects after others linked), and we still want the
    // Orders tab to reflect whatever did link.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['shipments', 'procurement-orders', shipmentId] });
      qc.invalidateQueries({ queryKey: ['shipments', 'detail', shipmentId] });
      // Refresh the PO-side card (procurement) and drop the now-linked PO from
      // the linkable picker — bidirectional invalidation across the A↔B link.
      qc.invalidateQueries({ queryKey: ['procurement-po-shipments'] });
      qc.invalidateQueries({ queryKey: ['shipments', 'linkable-purchase-orders'] });
      void reevaluateChecklist(shipmentId, qc);
    },
  });
}

// Contract H write-path — remove a PO↔shipment junction (soft-delete).
export function useUnlinkInboundDelivery(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (junctionId: string) => apiDelete(`${INBOUND_DELIVERY_API}/${junctionId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shipments', 'procurement-orders', shipmentId] });
      qc.invalidateQueries({ queryKey: ['shipments', 'detail', shipmentId] });
      // Refresh the PO-side card and restore the now-unlinked PO to the picker.
      qc.invalidateQueries({ queryKey: ['procurement-po-shipments'] });
      qc.invalidateQueries({ queryKey: ['shipments', 'linkable-purchase-orders'] });
      void reevaluateChecklist(shipmentId, qc);
    },
  });
}

const SALES_DELIVERY_API = '/api/v1/sales/deliveries';

/** Minimal sales-delivery shape the Orders-tab picker needs. Read over sales's
 * REST surface (never by importing sales code — frontend module boundary). */
export interface LinkableFulfilmentDoc {
  id: string;
  delivery_number: string;
  status: string;
  sales_order_id: string;
  sales_order_number: string;
  customer_name: string | null;
  item_count: number;
}

// Contract G write-path — eligible sales deliveries to link to this transport
// shipment (stock, not-yet-linked, non-cancelled). Served by the sales backend;
// the picker filters client-side.
export function useLinkableFulfilmentDocs() {
  return useQuery({
    queryKey: ['shipments', 'linkable-fulfilment-docs'],
    queryFn: () => apiGet<{ items: LinkableFulfilmentDoc[] }>(`${SALES_DELIVERY_API}/linkable`),
    select: (resp) => resp.items,
  });
}

// Contract G write-path — link one or more existing sales deliveries to this
// transport shipment in a single pass. The link FK is owned by sales; its
// transport-link endpoint already accepts a batch of ids, so we POST them all
// at once (with transport_shipment_id set, so they join this transport rather
// than creating a new one) and invalidate the shipment-side discovery query.
export function useLinkFulfilmentDocsToShipment(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deliveryIds: string[]) =>
      apiPost(`${SALES_DELIVERY_API}/transport-link`, {
        delivery_ids: deliveryIds,
        transport_shipment_id: shipmentId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shipments', 'fulfilment-docs', shipmentId] });
      qc.invalidateQueries({ queryKey: ['shipments', 'linkable-fulfilment-docs'] });
      qc.invalidateQueries({ queryKey: ['shipments', 'detail', shipmentId] });
    },
  });
}

// Contract G write-path — detach a sales delivery from this transport shipment.
export function useUnlinkFulfilmentDoc(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deliveryId: string) =>
      apiPost(`${SALES_DELIVERY_API}/${deliveryId}/unlink-transport`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shipments', 'fulfilment-docs', shipmentId] });
      qc.invalidateQueries({ queryKey: ['shipments', 'linkable-fulfilment-docs'] });
      qc.invalidateQueries({ queryKey: ['shipments', 'detail', shipmentId] });
    },
  });
}

// Phase 2 §4 — per-vendor-role AP cost rollup for this shipment.
export function useShipmentCostRollup(shipmentId: string) {
  return useQuery({
    queryKey: ['shipments', 'cost-rollup', shipmentId],
    queryFn: () => apiGet<ShipmentCostRollup>(`${SHIPMENTS_API}/${shipmentId}/cost-rollup`),
    enabled: !!shipmentId,
  });
}

// TMS B4 — job P&L rollup (three-view planned/actual/accrued + variance) for the
// shipment Financials tab. Persona-gated server-side (shipper gets cost-only).
export function useShipmentJobPnl(shipmentId: string) {
  return useQuery({
    queryKey: ['shipments', 'job-pnl', shipmentId],
    queryFn: () => apiGet<JobPnl>(`${SHIPMENTS_API}/${shipmentId}/job-pnl`),
    enabled: !!shipmentId,
  });
}

// ── TMS B7: job close / liquidation ───────────────────────────────────────

export function useJobCloseState(shipmentId: string) {
  return useQuery({
    queryKey: ['shipments', 'close-state', shipmentId],
    queryFn: () => apiGet<JobCloseState>(`${SHIPMENTS_API}/${shipmentId}/close-state`),
    enabled: !!shipmentId,
  });
}

// Invalidate everything a close/reopen changes: the close panel, the P&L rollup,
// the shipment header (status), and the list views.
function invalidateAfterClose(qc: ReturnType<typeof useQueryClient>, shipmentId: string) {
  qc.invalidateQueries({ queryKey: ['shipments', 'close-state', shipmentId] });
  qc.invalidateQueries({ queryKey: ['shipments', 'job-pnl', shipmentId] });
  qc.invalidateQueries({ queryKey: shipmentsKeys.detail(shipmentId) });
  qc.invalidateQueries({ queryKey: shipmentsKeys.all });
}

export function useCloseJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, overrideReason }: { shipmentId: string; overrideReason?: string }) =>
      apiPost(`${SHIPMENTS_API}/${shipmentId}/close`, { override_reason: overrideReason ?? null }),
    onSuccess: (_r, { shipmentId }) => invalidateAfterClose(qc, shipmentId),
  });
}

export function useReopenJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, reason }: { shipmentId: string; reason: string }) =>
      apiPost(`${SHIPMENTS_API}/${shipmentId}/reopen`, { reason }),
    onSuccess: (_r, { shipmentId }) => invalidateAfterClose(qc, shipmentId),
  });
}

// ── Diversion (sell-on-the-water destination change) ────────────────────────

/** A shipment's diversion history, newest first — actor, timestamp and before/after. */
export function useShipmentDiversions(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.diversions(shipmentId),
    queryFn: () =>
      apiGet<{ items: ShipmentDiversion[] }>(`${SHIPMENTS_API}/${shipmentId}/diversions`),
    enabled: !!shipmentId,
  });
}

/**
 * What diverting to the proposed destination/consignee WOULD do. A mutation
 * rather than a query because it is a POST whose answer depends on values the
 * user is still typing — but it writes nothing, so calling it is free.
 */
export function useShipmentDivertPreview() {
  return useMutation({
    mutationFn: ({ shipmentId, ...body }: { shipmentId: string } & ShipmentDivertRequest) =>
      apiPost<ShipmentDivertPreview>(`${SHIPMENTS_API}/${shipmentId}/divert/preview`, body),
  });
}

/**
 * Divert the shipment. One action changes the header, the inland leg, the
 * deliver-to party, the document set and the checklist — so it invalidates all
 * of them plus the exception list the customs warning lands in.
 */
export function useDivertShipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, ...body }: { shipmentId: string } & ShipmentDivertRequest) =>
      apiPost<ShipmentDivertResult>(`${SHIPMENTS_API}/${shipmentId}/divert`, body),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.diversions(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.detail(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.legs(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.documents(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.checklist(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.exceptions(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.parties(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.all });
    },
  });
}

/** Clear a diversion's reissue-required mark without re-issuing the document. */
export function useResolveDocumentReissueRequired() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, documentId }: { shipmentId: string; documentId: string }) =>
      apiPost(
        `${SHIPMENTS_API}/${shipmentId}/documents/${documentId}/reissue-required/resolve`,
        {},
      ),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.documents(shipmentId) });
    },
  });
}

// G2 credit control: manager release of a shipment credit hold (audited, reason required).
export function useReleaseShipmentCreditHold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, reason }: { shipmentId: string; reason: string }) =>
      apiPost(`${SHIPMENTS_API}/${shipmentId}/credit-release`, { reason }),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.detail(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.all });
    },
  });
}

// IMP-4 — per-PO, per-line landed-cost allocation across the shipment's orders.
export function useShipmentCostAllocation(shipmentId: string) {
  return useQuery({
    queryKey: ['shipments', 'cost-allocation', shipmentId],
    queryFn: () =>
      apiGet<ShipmentCostAllocation>(`${SHIPMENTS_API}/${shipmentId}/landed-cost`),
    enabled: !!shipmentId,
  });
}

// E4 §4.6 — the Costs tab's blended per-category summary: one row per landed-cost
// category, each tagged `estimated` or `actual`, with the variance where both sides
// are known. Projected from procurement's blended rollup server-side.
export function useShipmentCostSummary(shipmentId: string) {
  return useQuery({
    queryKey: ['shipments', 'cost-summary', shipmentId],
    queryFn: () =>
      apiGet<ShipmentCostSummary>(`${SHIPMENTS_API}/${shipmentId}/cost-summary`),
    enabled: !!shipmentId,
  });
}

// E4 §4.6 — whether the Costs tab shows its "no cost estimates yet" banner for THIS
// reader. Same predicate and same dismissal store as the `shipments.estimate_missing`
// nag, so the banner cannot disagree with the email it explains.
export function useShipmentEstimateReminder(shipmentId: string) {
  return useQuery({
    queryKey: ['shipments', 'estimate-reminder', shipmentId],
    queryFn: () =>
      apiGet<ShipmentEstimateReminder>(`${SHIPMENTS_API}/${shipmentId}/estimate-reminder`),
    enabled: !!shipmentId,
  });
}

export function useDismissShipmentEstimateReminder(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost<ShipmentEstimateReminder>(
        `${SHIPMENTS_API}/${shipmentId}/estimate-reminder/dismiss`,
        {},
      ),
    // The dismissal writes the reminders framework's own `reminder_states` row, but
    // NOTHING in the frontend queries that list — the bell reads the notifications
    // inbox, which a dismissal does not change. So the banner's own key is all
    // there is to refresh, and a speculative `['reminders']` invalidation would be
    // a key head no query owns: dead at runtime, and a new unmapped head for the
    // data-freshness registry to carry.
    onSuccess: (data) => {
      qc.setQueryData(['shipments', 'estimate-reminder', shipmentId], data);
    },
  });
}

// Phase 2 §4 — AP bills grouped under this shipment (vendor role, payment, TNUM).
export function useShipmentBills(shipmentId: string) {
  return useQuery({
    queryKey: ['shipments', 'bills', shipmentId],
    queryFn: () => apiGet<{ items: ShipmentBillRow[] }>(`${SHIPMENTS_API}/${shipmentId}/bills`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

// Attach-bill picker — attachable AP bills (non-voided, not already on this
// shipment). Served by the shipments backend (which reads accounting via its
// public service), so the shipments frontend stays module-boundary-clean.
export function useShipmentCandidateBills(shipmentId: string, search: string, enabled: boolean) {
  return useQuery({
    queryKey: ['shipments', 'candidate-bills', shipmentId, search],
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiGet<{ items: ShipmentCandidateBill[] }>(
        `${SHIPMENTS_API}/${shipmentId}/candidate-bills${search ? `?search=${encodeURIComponent(search)}` : ''}`,
      ),
    select: (data) => data.items,
    enabled: enabled && !!shipmentId,
  });
}

// Phase 2 §4 — group an existing AP bill under this shipment with a vendor role.
// The shipments backend delegates the write to accounting's BillService (never
// writes accounting_bills directly) and publishes ACCOUNTING_BILL_CHANGED so
// landed_unit_cost_in_base recomputes before the response returns. Invalidate the
// shipment cost-rollup + bills + candidate-bills queries.
export function useAttachBillToShipment(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ShipmentBillAttachRequest) =>
      apiPost<{ data: ShipmentBillAttachResult }>(`${SHIPMENTS_API}/${shipmentId}/bills/attach`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shipments', 'cost-rollup', shipmentId] });
      qc.invalidateQueries({ queryKey: ['shipments', 'cost-allocation', shipmentId] });
      qc.invalidateQueries({ queryKey: ['shipments', 'bills', shipmentId] });
      qc.invalidateQueries({ queryKey: ['shipments', 'candidate-bills', shipmentId] });
      // A posted, categorised bill is the ACTUAL side of the Costs tab's blended
      // summary and supersedes its whole category (E4 §4.3). Without this the
      // category the user just billed goes on reading "No bill yet" above the
      // bill they can see in the list below it.
      qc.invalidateQueries({ queryKey: ['shipments', 'cost-summary', shipmentId] });
    },
  });
}

// TMS B1 — AR invoices billing this freight shipment (forwarder sell-side revenue).
export function useShipmentInvoices(shipmentId: string) {
  return useQuery({
    queryKey: ['shipments', 'invoices', shipmentId],
    queryFn: () =>
      apiGet<{ items: ShipmentInvoiceRow[] }>(`${SHIPMENTS_API}/${shipmentId}/invoices`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

// Attach-invoice picker — attachable AR invoices (non-voided, not already linked
// to this shipment). Served by the shipments backend (reads accounting via its
// public InvoiceService), keeping the shipments frontend module-boundary-clean.
export function useShipmentCandidateInvoices(shipmentId: string, search: string, enabled: boolean) {
  return useQuery({
    queryKey: ['shipments', 'candidate-invoices', shipmentId, search],
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiGet<{ items: ShipmentCandidateInvoice[] }>(
        `${SHIPMENTS_API}/${shipmentId}/candidate-invoices${search ? `?search=${encodeURIComponent(search)}` : ''}`,
      ),
    select: (data) => data.items,
    enabled: enabled && !!shipmentId,
  });
}

// TMS B1 — link an existing AR invoice to this shipment as sell-side job revenue.
// The shipments backend delegates the write to accounting's InvoiceService (never
// writes accounting_invoices directly). No cost-rollup event — sell-side has no
// landed cost. Invalidate the shipment invoices + candidate-invoices queries.
export function useAttachInvoiceToShipment(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ShipmentInvoiceAttachRequest) =>
      apiPost<{ data: ShipmentInvoiceAttachResult }>(
        `${SHIPMENTS_API}/${shipmentId}/invoices/attach`,
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shipments', 'invoices', shipmentId] });
      qc.invalidateQueries({ queryKey: ['shipments', 'candidate-invoices', shipmentId] });
    },
  });
}

// TMS B1/B2 — the missing half of the attach path above: release an AR invoice
// from this shipment, whichever storage arm binds them (the invoice's own
// freight_shipment_id, and/or this shipment's sell charges). Gated EDITOR on the
// SHIPMENT server-side. A consolidated invoice that has already been issued is
// refused — releasing its charges would re-open them for invoicing.
export function useDetachInvoiceFromShipment(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) =>
      apiPost(`${SHIPMENTS_API}/${shipmentId}/invoices/detach`, { invoice_id: invoiceId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shipments', 'invoices', shipmentId] });
      qc.invalidateQueries({ queryKey: ['shipments', 'candidate-invoices', shipmentId] });
      // The charge arm flips `invoiced`/`invoice_id` back on this shipment's
      // sell charges, so the charges view and anything drafted from it are stale.
      qc.invalidateQueries({ queryKey: ['shipments', 'charges', shipmentId] });
      // And the INVOICE end renders the same relationship, in accounting, on its own
      // keys — `InvoiceShipmentLinkSection` on a detail page the user may well have
      // open in another tab. Invalidating only this side leaves it showing a link
      // this mutation just removed. Same literal-key idiom as the
      // ['procurement-po-shipments'] cross-module invalidation above.
      qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

// TMS B2 — preview the un-invoiced sell charges (grouped by currency, with
// resolved GL accounts + disbursement flags) that "Invoice this shipment" will
// draft. One shipment id = per-shipment; several (same customer) = consolidated.
export function useInvoiceableCharges(shipmentIds: string[], enabled: boolean) {
  const qs = shipmentIds.map((id) => `shipment_ids=${encodeURIComponent(id)}`).join('&');
  return useQuery({
    queryKey: ['shipments', 'invoiceable-charges', [...shipmentIds].sort()],
    queryFn: () => apiGet<InvoiceableChargesResponse>(`${SHIPMENTS_API}/invoiceable-charges?${qs}`),
    enabled: enabled && shipmentIds.length > 0,
  });
}

// TMS B2 — draft AR invoice(s) from the selected (or all) un-invoiced sell
// charges. One draft per distinct sell currency. Invalidates the affected
// shipments' invoices + charges + candidate-invoices caches.
export function useInvoiceFromCharges() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: InvoiceFromChargesRequest) =>
      apiPost<InvoiceFromChargesResult>(`${SHIPMENTS_API}/invoice-from-charges`, data),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({ queryKey: ['shipments', 'invoiceable-charges'] });
      for (const shipmentId of variables.shipment_ids) {
        qc.invalidateQueries({ queryKey: ['shipments', 'invoices', shipmentId] });
        qc.invalidateQueries({ queryKey: ['shipments', 'charges', shipmentId] });
        qc.invalidateQueries({ queryKey: ['shipments', 'candidate-invoices', shipmentId] });
      }
    },
  });
}

// TMS B3 — preview the un-billed buy charges of the given shipment(s), grouped by
// (vendor, currency), for the "Bill from vendor" dialog.
export function useBillableCharges(shipmentIds: string[], enabled: boolean) {
  const qs = shipmentIds.map((id) => `shipment_ids=${encodeURIComponent(id)}`).join('&');
  return useQuery({
    queryKey: ['shipments', 'billable-charges', [...shipmentIds].sort()],
    queryFn: () => apiGet<BillableChargesResponse>(`${SHIPMENTS_API}/billable-charges?${qs}`),
    enabled: enabled && shipmentIds.length > 0,
  });
}

// TMS B3 — draft AP bill(s) from the selected (or all) un-billed buy charges. One
// draft per distinct (vendor, currency). Invalidates the affected shipments' bills
// + charges + candidate-bills + cost-rollup caches.
export function useBillFromCharges() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: BillFromChargesRequest) =>
      apiPost<BillFromChargesResult>(`${SHIPMENTS_API}/bill-from-charges`, data),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({ queryKey: ['shipments', 'billable-charges'] });
      for (const shipmentId of variables.shipment_ids) {
        qc.invalidateQueries({ queryKey: ['shipments', 'bills', shipmentId] });
        qc.invalidateQueries({ queryKey: ['shipments', 'charges', shipmentId] });
        qc.invalidateQueries({ queryKey: ['shipments', 'candidate-bills', shipmentId] });
        qc.invalidateQueries({ queryKey: ['shipments', 'cost-rollup', shipmentId] });
        // Same reason as the attach path: a new bill is a new actual, and the
        // blended summary reads the actuals.
        qc.invalidateQueries({ queryKey: ['shipments', 'cost-summary', shipmentId] });
      }
    },
  });
}

export function useCreateShipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ShipmentCreate) => apiPost<Shipment>(SHIPMENTS_API, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.all });
      // Dashboard KPI widgets count this file — refetch them (WI 99e1538f).
      qc.invalidateQueries({ queryKey: shipmentsDashboardKeys.all });
    },
  });
}

export function useUpdateShipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ShipmentUpdate }) => apiPut<Shipment>(`${SHIPMENTS_API}/${id}`, data),
    onSuccess: (_r, { id }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.all });
      qc.invalidateQueries({ queryKey: shipmentsDashboardKeys.all });
    },
  });
}

export function useDeleteShipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`${SHIPMENTS_API}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.all });
      qc.invalidateQueries({ queryKey: shipmentsDashboardKeys.all });
    },
  });
}

// Phase 4 — Excel tracker import. dry_run validates + reports without persisting.
export function useImportShipmentsExcel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, dryRun }: { file: File; dryRun: boolean }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dry_run', String(dryRun));
      return apiPost<ShipmentImportResult>(`${SHIPMENTS_API}/import`, formData);
    },
    onSuccess: (result) => {
      if (!result.dry_run) qc.invalidateQueries({ queryKey: shipmentsKeys.all });
    },
  });
}

// Download the canonical .xlsx import template (headers + one example row). The
// bearer token can't ride a plain <a href>, so fetch the blob through the
// authenticated axios client and trigger a browser download. Modelled as a
// mutation so failures surface via the global error toast.
export function useDownloadShipmentImportTemplate() {
  return useMutation({
    mutationFn: async () => {
      const response = await axiosInstance.get(`${SHIPMENTS_API}/import/template`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(
        new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = 'shipment-import-template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    },
  });
}

export function useUpdateShipmentStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiPost<Shipment>(`${SHIPMENTS_API}/${id}/status`, { status }),
    onSuccess: (_r, { id }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.all });
      // The status flip changes every dashboard count — without this the KPI
      // widgets kept pre-transition zeros (WI 99e1538f "dashboard empty").
      qc.invalidateQueries({ queryKey: shipmentsDashboardKeys.all });
    },
  });
}

// ─── Legs ───

export function useShipmentLegs(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.legs(shipmentId),
    queryFn: () => apiGet<{ items: ShipmentLeg[] }>(`${SHIPMENTS_API}/${shipmentId}/legs`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

export function useCreateLeg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: ShipmentLegCreate }) =>
      apiPost<ShipmentLeg>(`${SHIPMENTS_API}/${shipmentId}/legs`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.legs(shipmentId) }); },
  });
}

export function useUpdateLeg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, legId, data }: { shipmentId: string; legId: string; data: ShipmentLegUpdate }) =>
      apiPut<ShipmentLeg>(`${SHIPMENTS_API}/${shipmentId}/legs/${legId}`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.legs(shipmentId) }); },
  });
}

export function useDeleteLeg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, legId }: { shipmentId: string; legId: string }) =>
      apiDelete(`${SHIPMENTS_API}/${shipmentId}/legs/${legId}`),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.legs(shipmentId) }); },
  });
}

// ─── Assignees ───

export function useShipmentAssignees(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.assignees(shipmentId),
    queryFn: () => apiGet<{ items: ShipmentAssignee[] }>(`${SHIPMENTS_API}/${shipmentId}/assignees`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

export function useCreateAssignee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: ShipmentAssigneeCreate }) =>
      apiPost<ShipmentAssignee>(`${SHIPMENTS_API}/${shipmentId}/assignees`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.assignees(shipmentId) }); },
  });
}

export function useDeleteAssignee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, assigneeId }: { shipmentId: string; assigneeId: string }) =>
      apiDelete(`${SHIPMENTS_API}/${shipmentId}/assignees/${assigneeId}`),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.assignees(shipmentId) }); },
  });
}

// ─── Parties ───

export function useShipmentParties(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.parties(shipmentId),
    queryFn: () => apiGet<{ items: ShipmentParty[] }>(`${SHIPMENTS_API}/${shipmentId}/parties`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

export function useCreateParty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: ShipmentPartyCreate }) =>
      apiPost<ShipmentParty>(`${SHIPMENTS_API}/${shipmentId}/parties`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.parties(shipmentId) }); },
  });
}

export function useDeleteParty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, partyId }: { shipmentId: string; partyId: string }) =>
      apiDelete(`${SHIPMENTS_API}/${shipmentId}/parties/${partyId}`),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.parties(shipmentId) }); },
  });
}

// ─── Services ───

export function useShipmentServices(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.services(shipmentId),
    queryFn: () => apiGet<{ items: ShipmentService[] }>(`${SHIPMENTS_API}/${shipmentId}/services`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

export function useCreateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: ShipmentServiceCreate }) =>
      apiPost<ShipmentService>(`${SHIPMENTS_API}/${shipmentId}/services`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.services(shipmentId) }); },
  });
}

export function useDeleteService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, serviceId }: { shipmentId: string; serviceId: string }) =>
      apiDelete(`${SHIPMENTS_API}/${shipmentId}/services/${serviceId}`),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.services(shipmentId) }); },
  });
}

// ─── Cargo ───

export function useShipmentCargo(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.cargo(shipmentId),
    queryFn: () => apiGet<{ items: ShipmentCargo[] }>(`${SHIPMENTS_API}/${shipmentId}/cargo`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

export function useCreateCargo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: ShipmentCargoCreate }) =>
      apiPost<ShipmentCargo>(`${SHIPMENTS_API}/${shipmentId}/cargo`, data),
    onSuccess: (_r, { shipmentId }) => { invalidateAfterCargoChange(qc, shipmentId); },
  });
}

export function useUpdateCargo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, cargoId, data }: { shipmentId: string; cargoId: string; data: ShipmentCargoUpdate }) =>
      apiPut<ShipmentCargo>(`${SHIPMENTS_API}/${shipmentId}/cargo/${cargoId}`, data),
    onSuccess: (_r, { shipmentId }) => { invalidateAfterCargoChange(qc, shipmentId); },
  });
}

export function useDeleteCargo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, cargoId }: { shipmentId: string; cargoId: string }) =>
      apiDelete(`${SHIPMENTS_API}/${shipmentId}/cargo/${cargoId}`),
    onSuccess: (_r, { shipmentId }) => { invalidateAfterCargoChange(qc, shipmentId); },
  });
}

// A cargo write recomputes the shipment's chargeable quantity (G4) and may flag
// weight/volume charge lines for re-rate, so refresh cargo + the shipment header
// + the charges list in lockstep.
function invalidateAfterCargoChange(qc: ReturnType<typeof useQueryClient>, shipmentId: string) {
  qc.invalidateQueries({ queryKey: shipmentsKeys.cargo(shipmentId) });
  qc.invalidateQueries({ queryKey: shipmentsKeys.detail(shipmentId) });
  qc.invalidateQueries({ queryKey: shipmentsKeys.charges(shipmentId) });
}

// ─── Cargo contents (the stuffing plan) ───

// Shipment-wide on purpose: what a line still has unassigned is
// `line.quantity - sum(links)` across EVERY unit, so a per-unit query could not
// answer it without one request per container.
export function useShipmentCargoContents(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.cargoContents(shipmentId),
    queryFn: () =>
      apiGet<{ items: ShipmentCargoContentsLink[] }>(`${SHIPMENTS_API}/${shipmentId}/cargo-contents`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

export function useAddCargoContentsLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, cargoId, data }: { shipmentId: string; cargoId: string; data: ShipmentCargoContentsLinkCreate }) =>
      apiPost<ShipmentCargoContentsLink>(`${SHIPMENTS_API}/${shipmentId}/cargo/${cargoId}/contents`, data),
    onSuccess: (_r, { shipmentId }) => { invalidateAfterContentsChange(qc, shipmentId); },
  });
}

export function useUpdateCargoContentsLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, cargoId, linkId, data }: { shipmentId: string; cargoId: string; linkId: string; data: ShipmentCargoContentsLinkUpdate }) =>
      apiPut<ShipmentCargoContentsLink>(`${SHIPMENTS_API}/${shipmentId}/cargo/${cargoId}/contents/${linkId}`, data),
    onSuccess: (_r, { shipmentId }) => { invalidateAfterContentsChange(qc, shipmentId); },
  });
}

export function useRemoveCargoContentsLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, cargoId, linkId }: { shipmentId: string; cargoId: string; linkId: string }) =>
      apiDelete(`${SHIPMENTS_API}/${shipmentId}/cargo/${cargoId}/contents/${linkId}`),
    onSuccess: (_r, { shipmentId }) => { invalidateAfterContentsChange(qc, shipmentId); },
  });
}

// Pull ONE unit out of a count row, so it becomes a thing contents can name.
export function useEnumerateCargoUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, cargoId, data }: { shipmentId: string; cargoId: string; data: ShipmentCargoEnumerate }) =>
      apiPost<ShipmentCargo>(`${SHIPMENTS_API}/${shipmentId}/cargo/${cargoId}/enumerate`, data),
    onSuccess: (_r, { shipmentId }) => { invalidateAfterCargoChange(qc, shipmentId); },
  });
}

// A link change moves the container book's origin set and the receipt prefill,
// but not the shipment's chargeable quantity — the goods are on the file either
// way, so this is deliberately narrower than invalidateAfterCargoChange.
function invalidateAfterContentsChange(qc: ReturnType<typeof useQueryClient>, shipmentId: string) {
  qc.invalidateQueries({ queryKey: shipmentsKeys.cargoContents(shipmentId) });
}

// ─── Line Items ───

export function useShipmentLineItems(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.lineItems(shipmentId),
    queryFn: () => apiGet<{ items: ShipmentLineItem[] }>(`${SHIPMENTS_API}/${shipmentId}/line-items`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

export function useCreateLineItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: ShipmentLineItemCreate }) =>
      apiPost<ShipmentLineItem>(`${SHIPMENTS_API}/${shipmentId}/line-items`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.lineItems(shipmentId) }); },
  });
}

export function useUpdateLineItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, lineItemId, data }: { shipmentId: string; lineItemId: string; data: ShipmentLineItemUpdate }) =>
      apiPut<ShipmentLineItem>(`${SHIPMENTS_API}/${shipmentId}/line-items/${lineItemId}`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.lineItems(shipmentId) }); },
  });
}

export function useDeleteLineItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, lineItemId }: { shipmentId: string; lineItemId: string }) =>
      apiDelete(`${SHIPMENTS_API}/${shipmentId}/line-items/${lineItemId}`),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.lineItems(shipmentId) }); },
  });
}

// ─── References ───

export function useShipmentReferences(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.references(shipmentId),
    queryFn: () => apiGet<{ items: ShipmentReference[] }>(`${SHIPMENTS_API}/${shipmentId}/references`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

export function useCreateReference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: ShipmentReferenceCreate }) =>
      apiPost<ShipmentReference>(`${SHIPMENTS_API}/${shipmentId}/references`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.references(shipmentId) }); },
  });
}

export function useDeleteReference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, referenceId }: { shipmentId: string; referenceId: string }) =>
      apiDelete(`${SHIPMENTS_API}/${shipmentId}/references/${referenceId}`),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.references(shipmentId) }); },
  });
}

// ─── Documents ───

export function useShipmentDocuments(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.documents(shipmentId),
    queryFn: () => apiGet<{ items: ShipmentDocument[] }>(`${SHIPMENTS_API}/${shipmentId}/documents`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

export function useCreateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: ShipmentDocumentCreate }) =>
      apiPost<ShipmentDocument>(`${SHIPMENTS_API}/${shipmentId}/documents`, data),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.documents(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.checklist(shipmentId) });
    },
  });
}

/** One logical document a dropped file was split into, and how far its extraction
 * has got. A part exists from the moment the file is segmented — BEFORE it is
 * extracted — which is what lets the drop panel show each document appearing with
 * its own progress rather than one whole-panel spinner. */
export interface ShipmentDocumentParsePart {
  /** ``pending`` = not extracted yet; ``done`` = it produced the intake item
   * below; ``skipped`` = the model was asked and could not read this span, OR the
   * part was never asked at all because nothing could type it
   * (``isUnclassifiedPart``). */
  state: ShipmentDocumentParsePartState;
  /** What the segment map typed this page span as. Known before extraction runs,
   * so the part can be labelled while it is still pending.
   * ``UNCLASSIFIED_DOCUMENT_TYPE`` means the model read it and could not place it
   * in the shipping-document family. */
  document_type: string;
  /** ``[start, end]`` (1-based, inclusive) when this part is one slice of a split
   * bundle; null when the part IS the whole file — which does NOT mean one page. */
  page_range: [number, number] | null;
  intake_item_id: string | null;
  /** The intent recorded on the intake item. May differ from ``document_type``:
   * the segment map's type is a first pass, the extraction is authoritative. */
  intent: string | null;
  validation_flag_count: number;
  /** The job this part was filed against — null when it could not be matched
   * (un-filed drops only; a job-file drop always carries its own shipment). */
  shipment_id: string | null;
  shipment_reference_number: string | null;
  /** True only when an UN-FILED part was matched to this job by its extracted refs. */
  auto_matched: boolean;
  /** Bookkeeping the per-part retry rebuilds an EMAIL envelope's sub-document from
   * — the 0-based attachment index, null meaning the message body. Null throughout
   * on any other file. Nothing in the UI reads it.
   *
   * OPTIONAL, matching the generated `ShipmentDocumentParsePart`: `job.parts` is a
   * schemaless JSONB blob that was never backfilled, so every part row written
   * before this key existed simply lacks it. Declaring it required here asserted
   * more than the wire guarantees. */
  email_attachment_index?: number | null;
}

/** The parts of a settled job the model was asked for and could not read — the
 * ONLY ones a retry re-extracts.
 *
 * Deliberately narrower than ``unprocessed_parts``, which also counts
 * sub-documents dropped while the file was being PLANNED (a span past the
 * tracked-part cap, a page range that would not slice, an email attachment in a
 * format the pipeline cannot read). Those never became a part row, so there is
 * nothing to rebuild them from and re-dropping the file stays their only remedy —
 * which is why the panel counts the two separately rather than offering a Retry
 * that would silently leave some of them behind. */
export function unreadPartCount(job: ShipmentDocumentParseJob): number {
  return job.parts.filter((part) => part.state === 'skipped' && !isUnclassifiedPart(part)).length;
}

/** The backend's token for "the model read this and it is not a shipping document
 * type we know" — `UNCLASSIFIED_DOCUMENT_TYPE` in
 * `shipment_document_intake_service.py`, and also the value the classify/segment
 * schemas already offer the model for "anything else". */
export const UNCLASSIFIED_DOCUMENT_TYPE = 'other';

/** An unclassified part: read fine, just not a type this pipeline extracts.
 *
 * It shares the ``skipped`` state with a part the model could not READ, and the two
 * need different copy and different affordances — a retry re-extracts against the
 * intent a part already carries and can do nothing here, so the Retry button must
 * not count one. The backend excludes them from its own retry set for the same
 * reason, and the two predicates have to agree or the panel offers a button the API
 * refuses with a 409. */
export function isUnclassifiedPart(part: ShipmentDocumentParsePart): boolean {
  return part.document_type === UNCLASSIFIED_DOCUMENT_TYPE;
}

/** How many of a job's parts were read but could not be typed. Counted separately
 * from ``unreadPartCount`` because re-dropping the file is not their remedy either
 * — the answer would be the same. */
export function unclassifiedPartCount(job: ShipmentDocumentParseJob): number {
  return job.parts.filter(isUnclassifiedPart).length;
}

/** One dropped file's background parse job — what the drop panel watches.
 *
 * Extraction runs in a background task, so the POST returns this record as
 * ``queued`` and the client polls it. It used to run inline, which capped one drop
 * at whatever fitted in the 60s gateway and handed the rest back as a "Parse N
 * remaining documents" button. */
export interface ShipmentDocumentParseJob {
  id: string;
  file_id: string;
  file_name: string;
  shipment_id: string | null;
  status: ShipmentDocumentParseStatus;
  /** Why a `failed` job failed — null on every other status. A closed vocabulary
   * of ours, so the panel translates it by code
   * (`shipmentDocumentParseFailureReasonI18nKey`); the server's own exception text
   * never travels. */
  failure_reason: ShipmentDocumentParseFailureReason | null;
  /** WHICH planning route decided `parts`. Four of them end in ONE whole-document
   * part and two of those mean the multi-document check never RAN, which is
   * otherwise indistinguishable from a genuine single document — see
   * `bundleCheckMissed`. Null until the parts are planned. */
  plan_path: ShipmentDocumentParsePlanPath | null;
  /** The logical documents this file was split into, in page order. Empty until
   * segmentation finishes. */
  parts: ShipmentDocumentParsePart[];
  /** Sub-documents this file produced no intake item for — past the tracked-span
   * cap, an unsliceable range, or a span the model could not read. */
  unprocessed_parts: number;
  created_at?: string | null;
  updated_at?: string | null;
}

/** A job in one of these states has stopped moving, so polling can stop. */
export const SETTLED_PARSE_STATUSES: readonly ShipmentDocumentParseStatus[] = [
  'done',
  'failed',
];

export function isParseJobSettled(job: ShipmentDocumentParseJob): boolean {
  return SETTLED_PARSE_STATUSES.includes(job.status);
}

/** The planning routes on which a multi-page file was read as ONE document WITHOUT
 * the multi-document check ever running. Derived from each route's own
 * `bundle_check_missed` flag in the config, never re-listed: the backend logs off
 * the same flag, and a hand-written copy of "which routes are the bad ones" drifts
 * the next time a route is added. */
const BUNDLE_CHECK_MISSED = new Set<string>(SHIPMENT_DOCUMENT_PARSE_PLAN_PATH_CHECK_MISSED_VALUES);

/**
 * This file was read as ONE document and never checked for several, so it may be a
 * pack whose other documents are unread.
 *
 * Lives here rather than in either surface that renders it — the drop panel and the
 * upload-attempts history both have to answer this, and a second copy of the
 * predicate is how the two would come to disagree about which job is worrying.
 *
 * Only on a SETTLED job: the route is not written until planning finishes, and
 * while a job is extracting the panel already says so. `failed` is INCLUDED, and is
 * the case that most needs it — a PDF whose page count we could not read is usually
 * one the model could not read either, so the job settles `failed` with
 * `no_document_extracted`, whose copy tells the operator a clearer scan will fix it.
 * For a password-protected file that is wrong, and the plan path is the only thing
 * on the row that knows why.
 */
export function bundleCheckMissed(job?: ShipmentDocumentParseJob): boolean {
  return Boolean(
    job && isParseJobSettled(job) && job.plan_path && BUNDLE_CHECK_MISSED.has(job.plan_path),
  );
}

/**
 * Docs Agent v1 (F3): QUEUE an already-uploaded document (CI/PL/BL/AWB/arrival
 * notice) for extraction into draft-first AI intake items. ``document_type`` is
 * optional — omit it to let the model classify.
 *
 * Returns the queued job immediately; the work happens in a background task and
 * the caller watches it with ``useShipmentDocParseJobs``. A bad drop (unknown
 * file, wrong format, a file already filed elsewhere) still fails synchronously
 * here, so the user gets a specific error rather than a job that fails later.
 *
 * Omit ``shipmentId`` for the **un-filed inbox**: a document with no target job.
 * The backend then matches it to a shipment by its extracted references, or leaves
 * it quarantined for an operator to file. Both routes return the same shape.
 */
export function useExtractShipmentDocument(shipmentId?: string) {
  return useMutation({
    mutationFn: (body: {
      file_id: string;
      document_type?: string;
      /**
       * Re-read bytes this extractor has already read. Absent, the backend HOLDS
       * the drop with `DOCUMENT_ALREADY_EXTRACTED` (409) naming where the earlier
       * result went, rather than silently re-paying for a full extraction and
       * minting a duplicate set of proposals — see `_require_no_earlier_extraction`.
       */
      confirm_duplicate?: boolean;
    }) =>
      apiPost<ShipmentDocumentParseJob>(
        shipmentId
          ? `${SHIPMENTS_API}/${shipmentId}/documents/extract`
          : `${SHIPMENTS_API}/document-inbox/extract`,
        body,
      ),
  });
}

/**
 * Re-extract only the parts of an existing parse job the model could not read.
 *
 * Runs against the file ALREADY uploaded — no second upload and no new `files`
 * row — and updates those parts' rows in place, so the parts that already
 * succeeded keep their intake items and the review queue gains exactly the
 * documents this pass recovers. Re-dropping the file instead uploads the same
 * bytes as a new file, which re-extracts every part of the pack and duplicates
 * the ones that worked.
 *
 * Returns the job flipped to `extracting`; the work happens in a background task
 * and the caller watches it through `useShipmentDocParseJobs`, which resumes
 * polling on its own once the job is unsettled again. A job that is still
 * extracting, or has nothing unread, is refused with a 409.
 *
 * Omit `shipmentId` for the un-filed inbox, exactly as the extract hook does.
 */
export function useRetryShipmentDocParts(shipmentId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) =>
      apiPost<ShipmentDocumentParseJob>(
        shipmentId
          ? `${SHIPMENTS_API}/${shipmentId}/documents/parse-jobs/${jobId}/retry`
          : `${SHIPMENTS_API}/document-inbox/parse-jobs/${jobId}/retry`,
        {},
      ),
    // The poll's `refetchInterval` stops itself once every job has settled, and it
    // is recomputed from the CACHED data — which still says `done` the moment this
    // returns. Without an invalidation the timer never re-arms and the retry runs
    // to completion behind a panel that shows none of it.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...shipmentsKeys.all, 'doc-parse-jobs'] });
    },
  });
}

/**
 * Poll the parse jobs for the files dropped in this panel.
 *
 * Polls every 2s (the same cadence the ingestion review table uses) while any job
 * is still moving, and stops once they have all settled — so an idle panel costs
 * nothing. Batched into ONE request over all dropped files; a request per file
 * would be an N+1 on a 2-second timer.
 *
 * ``live`` data is deliberately not persisted to the offline cache: a job status
 * is true for seconds, and rehydrating "extracting" from IndexedDB a day later
 * would show a spinner for work that finished long ago.
 *
 * ``fileIds`` must be the files still WORTH asking about — the caller drops one
 * whose queue call failed, because no job will ever exist for it.
 */
export function useShipmentDocParseJobs(shipmentId: string | undefined, fileIds: string[]) {
  const key = [...fileIds].sort();
  return useQuery({
    queryKey: [...shipmentsKeys.all, 'doc-parse-jobs', shipmentId ?? 'inbox', key],
    queryFn: () =>
      apiGet<ShipmentDocumentParseJob[]>(
        shipmentId
          ? `${SHIPMENTS_API}/${shipmentId}/documents/parse-jobs?file_ids=${key.join(',')}`
          : `${SHIPMENTS_API}/document-inbox/parse-jobs?file_ids=${key.join(',')}`,
      ),
    enabled: key.length > 0,
    meta: { persist: false },
    refetchInterval: (query) => {
      const jobs = query.state.data;
      if (!jobs) return POLL_INTERVAL_MS;
      // Keep polling until every REQUESTED file has a job AND all of them have
      // settled. Checking only `every(settled)` is vacuously true over a short
      // array, so a file whose job has not appeared yet would stop the timer the
      // moment the others finished — leaving its row spinning forever with
      // nothing left to advance it.
      if (jobs.length < key.length) return POLL_INTERVAL_MS;
      return jobs.every(isParseJobSettled) ? false : POLL_INTERVAL_MS;
    },
  });
}

/**
 * The recent parse jobs for this shipment (or, with no `shipmentId`, this user's
 * own un-filed drops) — asked for WITHOUT any file id.
 *
 * This is what lets the drop panel resume. `useShipmentDocParseJobs` above can only
 * ask about files whose ids the panel is holding, and those live in component state:
 * remount the panel and every trace of what was dropped is gone, including a drop
 * that FAILED. The row survived in `shp_document_parse_jobs` the whole time — with
 * its filename, its uploader and its timestamp — and until this call nothing could
 * reach it (TKT-00189: a document failed to upload during a support call and the
 * agent could find no record of it afterwards).
 *
 * Not polled: the poll hook takes over for anything still moving. It is not fetched
 * strictly once per mount either — seven shipment mutations in this file invalidate
 * `shipmentsKeys.all`, which prefix-matches this key and refetches it — so the
 * caller's merge must tolerate `history` changing mid-session (`orderedFileIds`
 * does: it filters out anything already in `droppedFileIds`, so a refetch cannot
 * duplicate or reorder a live row).
 *
 * Not persisted offline for the same reason the poll is not — a settled-days-ago job
 * list rehydrated from IndexedDB would be describing work nobody is watching.
 */
export function useShipmentDocParseJobHistory(shipmentId: string | undefined) {
  return useQuery({
    queryKey: [...shipmentsKeys.all, 'doc-parse-job-history', shipmentId ?? 'inbox'],
    queryFn: () =>
      apiGet<ShipmentDocumentParseJob[]>(
        shipmentId
          ? `${SHIPMENTS_API}/${shipmentId}/documents/parse-jobs`
          : `${SHIPMENTS_API}/document-inbox/parse-jobs`,
      ),
    meta: { persist: false },
  });
}

/** One page of upload-attempt history, plus the unpaginated `total` a pager needs. */
export interface ShipmentDocumentParseJobPage {
  items: ShipmentDocumentParseJob[];
  /** Attempts matching the same scope and filter across ALL pages — never the
   * page's own length, which is what makes a truncated first page look complete. */
  total: number;
}

/**
 * The BROWSABLE upload-attempt history — the paged set behind the window above.
 *
 * `useShipmentDocParseJobHistory` answers a fixed recent window
 * (`PARSE_JOB_HISTORY_LIMIT` = 25, newest first, no pager), which is all the drop
 * panel should ever render: it is a recent-activity feed and its density budget is
 * spent. But `shp_document_parse_jobs` ACCUMULATES — one row per distinct file ever
 * dropped here, never deleted — so past 25 documents an older FAILED attempt fell
 * out of that window, and no other surface in the app lists parse jobs. This is the
 * route that reaches it, `?limit=&offset=&failed_only=`.
 *
 * Omit `shipmentId` for the un-filed inbox twin, exactly as the hooks above do; the
 * backend scopes that one to the DROPPER on every page, since it has no per-record
 * gate of its own.
 *
 * Not persisted offline (a settled-days-ago attempt list rehydrated from IndexedDB
 * would describe work nobody is watching) and not polled — anything still moving is
 * the poll hook's job, and this surface is opened to look BACKWARDS.
 */
export function useShipmentDocParseJobAttempts(
  shipmentId: string | undefined,
  params: { limit: number; offset: number; failedOnly: boolean },
  enabled = true,
) {
  const query = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
    ...(params.failedOnly ? { failed_only: 'true' } : {}),
  });
  return useQuery({
    queryKey: [
      ...shipmentsKeys.all,
      'doc-parse-job-attempts',
      shipmentId ?? 'inbox',
      params.limit,
      params.offset,
      params.failedOnly,
    ],
    queryFn: () =>
      apiGet<ShipmentDocumentParseJobPage>(
        shipmentId
          ? `${SHIPMENTS_API}/${shipmentId}/documents/parse-jobs/history?${query}`
          : `${SHIPMENTS_API}/document-inbox/parse-jobs/history?${query}`,
      ),
    enabled,
    meta: { persist: false },
  });
}

export function useUpdateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, documentId, data }: { shipmentId: string; documentId: string; data: ShipmentDocumentUpdate }) =>
      apiPut<ShipmentDocument>(`${SHIPMENTS_API}/${shipmentId}/documents/${documentId}`, data),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.documents(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.checklist(shipmentId) });
    },
  });
}

/**
 * The tenant's own document types — the extra options every document-type picker offers
 * beside the built-in ones.
 *
 * Shape B (`ui-list-pages.md` § 11): the route returns the complete set and every
 * consumer slices client-side, because a tenant curates a handful of these and a hidden
 * cap would silently drop one off the end of a picker — which reads to the user as "my
 * type is gone", not as a page 2.
 */
export function useTenantDocumentTypes(includeInactive = false) {
  return useQuery({
    queryKey: shipmentsKeys.tenantDocumentTypes(includeInactive),
    queryFn: () =>
      apiGet<{ data: TenantDocumentType[] }>(
        `${SHIPMENTS_API}/document-types${includeInactive ? '?include_inactive=true' : ''}`,
      ).then((r) => r.data),
  });
}

/**
 * Tenant document-type CRUD — the writer behind the Shipments settings card.
 *
 * Every mutation invalidates the whole `tenantDocumentTypes` key prefix rather than
 * one `includeInactive` variant: the settings card reads the include-inactive list and
 * every picker reads the active-only one, so retiring a type from the card has to drop
 * it out of the pickers in the same beat. Admin-gated on the backend (`require_admin`),
 * which is why the card only renders behind `useIsModuleAdmin('shipments')`.
 */
const TENANT_DOCUMENT_TYPES_KEY = ['shipments', 'tenantDocumentTypes'] as const;

export function useCreateTenantDocumentType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: TenantDocumentTypeCreate) =>
      apiPost<TenantDocumentType>(`${SHIPMENTS_API}/document-types`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: TENANT_DOCUMENT_TYPES_KEY }),
  });
}

export function useUpdateTenantDocumentType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TenantDocumentTypeUpdate }) =>
      apiPut<TenantDocumentType>(`${SHIPMENTS_API}/document-types/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: TENANT_DOCUMENT_TYPES_KEY }),
  });
}

/**
 * Delete a type nothing references; RETIRE one that documents already carry. The
 * response says which happened, so the card can tell the user — they are different
 * outcomes and a "deleted" toast over a type still on the list reads as a bug.
 */
export function useDeleteTenantDocumentType() {
  const qc = useQueryClient();
  return useMutation({
    // `apiRequest`, not `apiDelete`: this DELETE answers a body and `apiDelete`
    // is typed `Promise<void>` for its several dozen other callers.
    mutationFn: (id: string) =>
      apiRequest<TenantDocumentTypeDeleteResult>(
        'DELETE',
        `${SHIPMENTS_API}/document-types/${id}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: TENANT_DOCUMENT_TYPES_KEY }),
  });
}

/**
 * Answer one unrecognised document: file it under a type, keep it as an attachment, or
 * stop asking.
 *
 * Invalidates the intake queue (the row leaves the "needs your decision" list), the
 * documents list and the checklist (filing one may answer an open plan item, which moves
 * the completion percent).
 */
export function useResolveDocumentTriage(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      body,
    }: {
      itemId: string;
      body: ShipmentDocumentTriageResolve;
    }) =>
      apiPost(`${SHIPMENTS_API}/${shipmentId}/documents/triage/${itemId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shipmentDocIntakeKey(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.documents(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.checklist(shipmentId) });
    },
  });
}

export function useTransitionDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      shipmentId,
      documentId,
      data,
    }: {
      shipmentId: string;
      documentId: string;
      data: ShipmentDocumentTransition;
    }) =>
      apiPost<ShipmentDocument>(
        `${SHIPMENTS_API}/${shipmentId}/documents/${documentId}/transition`,
        data,
      ),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.documents(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.checklist(shipmentId) });
    },
  });
}

/** The file's governing house-bill release state (null when no negotiable bill). */
export function useShipmentBlRelease(shipmentId: string, enabled = true) {
  return useQuery({
    queryKey: shipmentsKeys.blRelease(shipmentId),
    queryFn: () => apiGet<BLReleaseState | null>(`${SHIPMENTS_API}/${shipmentId}/bl-release`),
    enabled: !!shipmentId && enabled,
  });
}

/** A single transport document's B/L release-control state + transition trail. */
export function useDocumentReleaseState(shipmentId: string, documentId: string, enabled = true) {
  return useQuery({
    queryKey: shipmentsKeys.documentRelease(shipmentId, documentId),
    queryFn: () =>
      apiGet<BLReleaseState>(`${SHIPMENTS_API}/${shipmentId}/documents/${documentId}/release`),
    enabled: !!shipmentId && !!documentId && enabled,
  });
}

/** Advance a transport document's release-control state machine by one step. */
export function useTransitionDocumentRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      shipmentId,
      documentId,
      data,
    }: {
      shipmentId: string;
      documentId: string;
      data: DocumentReleaseTransition;
    }) =>
      apiPost<BLReleaseState>(
        `${SHIPMENTS_API}/${shipmentId}/documents/${documentId}/release/transition`,
        data,
      ),
    onSuccess: (_r, { shipmentId, documentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.documentRelease(shipmentId, documentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.blRelease(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.documents(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.exceptions(shipmentId) });
    },
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, documentId }: { shipmentId: string; documentId: string }) =>
      apiDelete(`${SHIPMENTS_API}/${shipmentId}/documents/${documentId}`),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.documents(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.checklist(shipmentId) });
    },
  });
}

// Fetch a PDF blob through the authenticated axios client (the bearer token
// can't ride a plain link) and trigger a browser download.
async function downloadPdfBlob(
  path: string,
  fileName: string,
  params?: Record<string, string>,
): Promise<void> {
  const response = await axiosInstance.get(path, { responseType: 'blob', params });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fileName.replace(/\//g, '-')}.pdf`;
  a.click();
  window.URL.revokeObjectURL(url);
}

// Download the branded quotation PDF for a QUOTATION document. `layoutId` is the
// named line-column layout chosen at generation time; omitted resolves the
// company/tenant default (see docs/platform/document-design-service.md).
export async function downloadQuotationPdf(
  shipmentId: string,
  documentId: string,
  fileName: string,
  layoutId?: string | null,
): Promise<void> {
  await downloadPdfBlob(
    `${SHIPMENTS_API}/${shipmentId}/documents/${documentId}/quotation/pdf`,
    fileName,
    layoutId ? { layout_id: layoutId } : undefined,
  );
}

// Document-design registry key the backend registered for the branded quotation
// (`DOCUMENT_TYPE` in modules/shipments/services/document_design_provider.py).
// It selects the named line-column layouts a `DocumentLayoutPicker` offers.
export const QUOTATION_DOCUMENT_TYPE = 'shipment_quotation';

// Path of the branded quotation preview HTML (fed to `useDocumentPreviewHtml`).
export function quotationPreviewPath(shipmentId: string, documentId: string): string {
  return `${SHIPMENTS_API}/${shipmentId}/documents/${documentId}/quotation/preview`;
}

// Email the branded quotation PDF to the shipment's customer through the unified
// outbound seam (user mailbox when enabled, else platform ESP). The backend
// renders the PDF and resolves the default recipient from the customer org.
export interface ShipmentQuotationEmailRequest {
  to_email?: string;
  cc_emails?: string[];
  subject?: string;
  message?: string;
  /** Named line-column layout to render with; omitted takes the type's default. */
  layout_id?: string;
}

export function useEmailQuotation() {
  return useMutation({
    mutationFn: ({
      shipmentId,
      documentId,
      data,
    }: {
      shipmentId: string;
      documentId: string;
      data: ShipmentQuotationEmailRequest;
    }) =>
      apiPost(
        `${SHIPMENTS_API}/${shipmentId}/documents/${documentId}/quotation/email`,
        data,
      ),
  });
}

// Download a branded freight-document PDF (HBL/HAWB/arrival notice/delivery
// order). House BLs/AWBs are forwarder-only on the backend (404 otherwise).
export async function downloadFreightDocumentPdf(
  shipmentId: string,
  documentId: string,
  fileName: string,
): Promise<void> {
  await downloadPdfBlob(
    `${SHIPMENTS_API}/${shipmentId}/documents/${documentId}/freight/pdf`,
    fileName,
  );
}

// Path of the branded freight-document preview HTML (fed to
// `useDocumentPreviewHtml`), the read-only twin of `downloadFreightDocumentPdf`.
// Unlike the quotation preview it takes no `layout_id`: freight documents have no
// named line-column layouts, only the branding cascade and `language`.
export function freightPreviewPath(shipmentId: string, documentId: string): string {
  return `${SHIPMENTS_API}/${shipmentId}/documents/${documentId}/freight/preview`;
}

// One-click generate-and-dispatch of an operational document (arrival notice /
// delivery order): the backend renders the branded PDF, issues the document,
// emails it to the consignee/notify party (to_email overrides), and ticks the
// checklist. Forwarder-only. A blocked delivery order returns 400
// SHIPMENT_DELIVERY_ORDER_NOT_RELEASABLE with the unmet conditions in details.
export interface ShipmentDocumentDispatchRequest {
  to_email?: string;
  cc_emails?: string[];
  subject?: string;
  message?: string;
}

export interface ShipmentDocumentDispatchResult {
  document: ShipmentDocument;
  sent: { to: string; outbound_email_id?: string; channel?: string };
}

export function useDispatchDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      shipmentId,
      documentId,
      data,
    }: {
      shipmentId: string;
      documentId: string;
      data: ShipmentDocumentDispatchRequest;
    }) =>
      apiPost<ShipmentDocumentDispatchResult>(
        `${SHIPMENTS_API}/${shipmentId}/documents/${documentId}/dispatch`,
        data,
      ),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.documents(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.checklist(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.deliveryReleaseStatus(shipmentId) });
    },
  });
}

// Delivery-order release readiness: whether the shipment's customs is released
// and its house bill's surrender obligation is met, with per-condition reasons
// for the UI to explain a block. Forwarder-only.
export interface DeliveryReleaseCondition {
  code: string;
  met: boolean;
  customs_status?: string;
  release_type?: string | null;
  payment_terms?: string | null;
  requires_surrender?: boolean;
  surrendered?: boolean;
}

export interface DeliveryReleaseStatus {
  can_release: boolean;
  conditions: DeliveryReleaseCondition[];
}

export function useDeliveryOrderReleaseStatus(shipmentId: string, enabled: boolean) {
  return useQuery({
    queryKey: shipmentsKeys.deliveryReleaseStatus(shipmentId),
    queryFn: () =>
      apiGet<DeliveryReleaseStatus>(
        `${SHIPMENTS_API}/${shipmentId}/delivery-order/release-status`,
      ),
    enabled,
  });
}

// Download the branded cargo-manifest PDF for a consol (forwarder-only).
export async function downloadConsolManifestPdf(
  consolId: string,
  fileName: string,
): Promise<void> {
  await downloadPdfBlob(`/api/v1/consols/${consolId}/manifest/pdf`, fileName);
}

// Re-issue a document (version++ and optional switch-bill party substitution).
// Prior versions stay retrievable via /api/v1/versions/shp_shipment_documents/{id}.
export function useReissueDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      shipmentId,
      documentId,
      data,
    }: {
      shipmentId: string;
      documentId: string;
      data: ShipmentDocumentReissue;
    }) =>
      apiPost<ShipmentDocument>(
        `${SHIPMENTS_API}/${shipmentId}/documents/${documentId}/reissue`,
        data,
      ),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.documents(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.checklist(shipmentId) });
    },
  });
}

// ─── Checklist templates (Phase 3) ───

// The document + milestone checklist for a shipment, with completion %.
export function useShipmentChecklist(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.checklist(shipmentId),
    queryFn: () => apiGet<ShipmentChecklist>(`${SHIPMENTS_API}/${shipmentId}/checklist`),
    enabled: !!shipmentId,
  });
}

// Active checklist templates; when shape filters are supplied the response also
// flags which template the backend would suggest (suggested_code).
export function useChecklistTemplates(params?: ListChecklistTemplatesParams) {
  return useQuery({
    queryKey: shipmentsKeys.checklistTemplates(params),
    queryFn: () =>
      apiGet<ChecklistTemplatesResponse>(`${REF_DATA_API}/checklist-templates${buildQueryString(params ?? {})}`),
  });
}

// Apply (or re-apply, idempotently) a template to a shipment — pre-creates the
// expected documents + milestones.
export function useApplyChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, templateCode }: { shipmentId: string; templateCode: string }) =>
      apiPost<ShipmentChecklist>(`${SHIPMENTS_API}/${shipmentId}/checklist/apply`, { template_code: templateCode }),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.checklist(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.documents(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.milestones(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.detail(shipmentId) });
    },
  });
}

// ─── Charges ───

// `enabled` is the caller's own gate ON TOP of having a shipment id — the Costs
// tab reads charges only for a forwarder tenant, whose Charges tab is the surface
// that creates them; an importer tenant has none, so the request is pure waste.
export function useShipmentCharges(shipmentId: string, enabled = true) {
  return useQuery({
    queryKey: shipmentsKeys.charges(shipmentId),
    queryFn: () => apiGet<ShipmentChargeList>(`${SHIPMENTS_API}/${shipmentId}/charges`),
    select: (data) => data.items,
    enabled: enabled && !!shipmentId,
  });
}

export function useCreateCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: ShipmentChargeCreate }) =>
      apiPost<ShipmentCharge>(`${SHIPMENTS_API}/${shipmentId}/charges`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.charges(shipmentId) }); },
  });
}

export function useUpdateCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, chargeId, data }: { shipmentId: string; chargeId: string; data: ShipmentChargeUpdate }) =>
      apiPut<ShipmentCharge>(`${SHIPMENTS_API}/${shipmentId}/charges/${chargeId}`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.charges(shipmentId) }); },
  });
}

export function useDeleteCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, chargeId }: { shipmentId: string; chargeId: string }) =>
      apiDelete(`${SHIPMENTS_API}/${shipmentId}/charges/${chargeId}`),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.charges(shipmentId) }); },
  });
}

// Tenant charge-code master (drives the charge dialog's code picker). The list
// is small and tenant-scoped, so it's fetched whole and filtered client-side.
// The endpoint returns a bare `{ items }` envelope (not the `ListResponse`
// `{ data, count }` shape used by the global reference-data routes).
export function useChargeCodes(params?: ListChargeCodesParams, enabled = true) {
  return useQuery({
    queryKey: shipmentsKeys.chargeCodes(params),
    queryFn: () => apiGet<{ items: ChargeCode[] }>(`${SHIPMENTS_API}/charge-codes${buildQueryString(params ?? {})}`),
    select: (data) => data.items,
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

// Charge-code master CRUD — maintained from the "Setup" → Charge Codes page.
// Invalidates every charge-code list so the shipment Charges-tab picker picks
// up new/edited codes immediately.
export function useCreateChargeCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ChargeCodeCreate) =>
      apiPost<ChargeCode>(`${SHIPMENTS_API}/charge-codes`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipments', 'charge-codes'] }),
  });
}

export function useUpdateChargeCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ChargeCodeUpdate }) =>
      apiPut<ChargeCode>(`${SHIPMENTS_API}/charge-codes/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipments', 'charge-codes'] }),
  });
}

export function useDeleteChargeCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`${SHIPMENTS_API}/charge-codes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipments', 'charge-codes'] }),
  });
}

// Bulk-loads the curated standard freight charge-code set. Idempotent on the
// backend (skips codes that already exist by code); returns how many were
// created vs. skipped, plus how many existing unmapped codes had their GL
// anchors back-filled from the chart of accounts, so the page can report the
// outcome.
export function useLoadStandardChargeCodes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost<{ created: number; skipped: number; mapped: number }>(
        `${SHIPMENTS_API}/charge-codes/load-standard`,
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipments', 'charge-codes'] }),
  });
}

// ─── Demurrage & Detention (D&D — D2 clocks + D3 disputes/terms) ───

export interface DndQueueParams {
  status?: string[];
  charge_type?: string[];
  limit?: number;
  offset?: number;
}

// Per-shipment clock panel (D&D tab). Clocks are recompute-only on the backend,
// so this is a pure read; the enrichment (container/equipment, open-dispute
// count) is batch-loaded server-side.
export function useShipmentDndClocks(shipmentId: string) {
  return useQuery({
    queryKey: ['shipments', 'dnd-clocks', shipmentId],
    queryFn: () => apiGet<{ items: ContainerDndClock[] }>(`${SHIPMENTS_API}/${shipmentId}/dnd-clocks`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

// Tenant-wide at-risk queue. The backend orders at-risk/accruing first then LFD
// ascending (id tiebreak); status / charge-type filters are repeatable query
// params. Clocks accumulate, so the queue page passes limit/offset and drives its
// pager from `total` — the unpaginated count for the same filters.
export function useDndQueue(params?: DndQueueParams) {
  return useQuery({
    queryKey: ['shipments', 'dnd-queue', params],
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiGet<{ items: ContainerDndClock[]; total: number }>(
        `${SHIPMENTS_API}/dnd/queue${buildQueryString(params ?? {})}`,
      ),
  });
}

export interface ExceptionQueueParams {
  exception_type?: string[];
  severity?: string[];
  status?: string[];
  /** Owner filter: 'mine' (assigned to me), 'unassigned' (no owner), else all. */
  assignment?: string;
  /** Age-in-state bucket filter: d0_1 / d2_3 / d4_7 / d7_plus. */
  age_bucket?: string;
  /** Page size. Omitted by the grouped view, which needs the whole open set. */
  limit?: number;
  offset?: number;
}

// Tenant-wide ops triage queue. The backend orders open exceptions by risk_score
// desc (money-at-risk x customer tier), with an id tiebreak; type / severity /
// status / owner / aging filters are query params. Exceptions accumulate, so the
// FLAT table view passes limit/offset and drives its pager from `total`; the
// GROUPED view omits limit and keeps the whole (bounded) open set, where
// `truncated` still flags that the server-side cap was hit.
export function useExceptionQueue(params?: ExceptionQueueParams) {
  return useQuery({
    queryKey: ['shipments', 'exception-queue', params],
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiGet<{ items: ExceptionQueueRow[]; total: number; truncated: boolean }>(
        `${SHIPMENTS_API}/exceptions/queue${buildQueryString(params ?? {})}`,
      ),
  });
}

// Server-side GROUP BY rollup for the re-pivotable board (control-tower v2c):
// per-group count + money-at-risk over the FULL open set (uncapped), so section
// totals stay accurate even when the flat queue is truncated.
export function useExceptionQueueGroups(
  groupBy: ExceptionGroupBy | null,
  params?: ExceptionQueueParams,
) {
  return useQuery({
    queryKey: ['shipments', 'exception-queue-groups', groupBy, params],
    queryFn: () =>
      apiGet<ExceptionGroupSummary>(
        `${SHIPMENTS_API}/exceptions/queue/groups${buildQueryString({
          group_by: groupBy ?? undefined,
          ...(params ?? {}),
        })}`,
      ),
    enabled: !!groupBy,
  });
}

// The aging band: open-exception counts per age-in-state bucket. Ignores any
// age_bucket filter server-side (it IS the per-bucket breakdown).
export function useExceptionQueueAging(params?: ExceptionQueueParams) {
  // The band shows the breakdown across ALL ages, so drop the age filter itself.
  const { age_bucket: _ignored, ...rest } = params ?? {};
  return useQuery({
    queryKey: ['shipments', 'exception-queue-aging', rest],
    queryFn: () =>
      apiGet<ExceptionAgingSummary>(
        `${SHIPMENTS_API}/exceptions/queue/aging${buildQueryString(rest)}`,
      ),
  });
}

// Uninvoiced-WIP (revenue leakage) list — one row per shipment carrying
// un-invoiced sell charges, oldest ready-to-invoice first. `age_bucket` narrows
// to one age-since-ready band; `truncated` flags the server-side cap was hit.
export interface UninvoicedWipParams {
  age_bucket?: string;
  limit: number;
  offset: number;
  sort_by: string;
  sort_direction: 'asc' | 'desc';
}

// Server-paged (§ 11 Shape A): the caller owns the window, so `limit`/`offset`
// are required — relying on the endpoint default would silently re-introduce a
// fixed slab the pager can't move.
export function useUninvoicedWip(params: UninvoicedWipParams) {
  return useQuery({
    queryKey: ['shipments', 'uninvoiced-wip', params],
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiGet<UninvoicedWipResponse>(
        `${SHIPMENTS_API}/uninvoiced-wip${buildQueryString({ ...params })}`,
      ),
  });
}

// The WIP aging band: shipment counts per age-since-ready bucket over the full
// open set (ignores any age filter — it IS the breakdown).
export function useUninvoicedWipAging() {
  return useQuery({
    queryKey: ['shipments', 'uninvoiced-wip-aging'],
    queryFn: () => apiGet<ExceptionAgingSummary>(`${SHIPMENTS_API}/uninvoiced-wip/aging`),
  });
}

// Invalidate every exception surface after a lifecycle write — a transition or
// (re)assignment changes the queue, its group rollup + aging band, the
// command-center widget, and the open-exceptions KPI, so all refresh together.
// The shipment's OWN exception log (Journey tab) is keyed per shipment and sits
// under none of those prefixes, so it takes an explicit key — without it a row
// dismissed from that table lingers until a full page reload.
function invalidateExceptions(qc: ReturnType<typeof useQueryClient>, shipmentId: string) {
  qc.invalidateQueries({ queryKey: shipmentsKeys.exceptions(shipmentId) });
  qc.invalidateQueries({ queryKey: ['shipments', 'exception-queue'] });
  qc.invalidateQueries({ queryKey: ['shipments', 'exception-queue-groups'] });
  qc.invalidateQueries({ queryKey: ['shipments', 'exception-queue-aging'] });
  qc.invalidateQueries({ queryKey: ['shipments-dashboard'] });
}

// Move an exception through its work-queue lifecycle (acknowledge / start /
// resolve-with-note / dismiss). The backend enforces the config transition graph.
export function useTransitionException() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      shipmentId,
      exceptionId,
      status,
      resolution_notes,
    }: {
      shipmentId: string;
      exceptionId: string;
      status: string;
      resolution_notes?: string | null;
    }) =>
      apiPut<ShipmentException>(
        `${SHIPMENTS_API}/${shipmentId}/exceptions/${exceptionId}/transition`,
        { status, resolution_notes: resolution_notes ?? null },
      ),
    onSuccess: (_r, { shipmentId }) => invalidateExceptions(qc, shipmentId),
  });
}

// Claim / reassign / unassign an exception's ops owner (null = unassign).
export function useAssignException() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      shipmentId,
      exceptionId,
      assignee_user_id,
    }: {
      shipmentId: string;
      exceptionId: string;
      assignee_user_id: string | null;
    }) =>
      apiPut<ShipmentException>(
        `${SHIPMENTS_API}/${shipmentId}/exceptions/${exceptionId}/assign`,
        { assignee_user_id },
      ),
    onSuccess: (_r, { shipmentId }) => invalidateExceptions(qc, shipmentId),
  });
}

// Disputes for one clock (the D&D-tab dispute dialog lists + edits these).
export function useClockDisputes(clockId: string | null) {
  return useQuery({
    queryKey: ['shipments', 'dnd-disputes', clockId],
    queryFn: () => apiGet<{ items: DndDispute[] }>(`${SHIPMENTS_API}/dnd-clocks/${clockId}/disputes`),
    select: (data) => data.items,
    enabled: !!clockId,
  });
}

// Invalidate every D&D read after a dispute write — raising / resolving a
// dispute flips its clock's status, so the panel + queue must refresh too.
function invalidateDnd(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['shipments', 'dnd-clocks'] });
  qc.invalidateQueries({ queryKey: ['shipments', 'dnd-queue'] });
  qc.invalidateQueries({ queryKey: ['shipments', 'dnd-disputes'] });
}

export function useCreateDndDispute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clockId, data }: { clockId: string; data: DndDisputeCreate }) =>
      apiPost<DndDispute>(`${SHIPMENTS_API}/dnd-clocks/${clockId}/disputes`, data),
    onSuccess: () => invalidateDnd(qc),
  });
}

export function useUpdateDndDispute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DndDisputeUpdate }) =>
      apiPut<DndDispute>(`${SHIPMENTS_API}/dnd-disputes/${id}`, data),
    onSuccess: () => invalidateDnd(qc),
  });
}

// ─── Export-control records (Wave 5, §6.11) ───
// The ECCN / licence / AES-ITN a human recorded on the file. Writes are keyed on the
// record; the read is keyed on the shipment, so one invalidation covers both.
const exportControlKey = (shipmentId: string) =>
  ['shipments', 'export-control-records', shipmentId] as const;

export function useExportControlRecords(shipmentId: string) {
  return useQuery({
    queryKey: exportControlKey(shipmentId),
    queryFn: () =>
      apiGet<{ items: ExportControlRecord[] }>(
        `${SHIPMENTS_API}/${shipmentId}/export-control-records`
      ),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

export function useCreateExportControlRecord(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ExportControlRecordCreate) =>
      apiPost<ExportControlRecord>(
        `${SHIPMENTS_API}/${shipmentId}/export-control-records`,
        data
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: exportControlKey(shipmentId) }),
  });
}

export function useUpdateExportControlRecord(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ExportControlRecordUpdate }) =>
      apiPut<ExportControlRecord>(`${SHIPMENTS_API}/export-control-records/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: exportControlKey(shipmentId) }),
  });
}

export function useDeleteExportControlRecord(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`${SHIPMENTS_API}/export-control-records/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: exportControlKey(shipmentId) }),
  });
}

// ─── Dangerous-goods declarations (Wave 5, §6.11) ───
// The list read returns each declaration WITH its lines, so every mutation — header,
// line, signature — invalidates the one shipment-keyed query. Signing freezes the
// declaration server-side, so the refetched `status` is what disables the edit
// affordances; the UI never tracks that state independently.
const dgDeclarationsKey = (shipmentId: string) =>
  ['shipments', 'dg-declarations', shipmentId] as const;

export function useDgDeclarations(shipmentId: string) {
  return useQuery({
    queryKey: dgDeclarationsKey(shipmentId),
    queryFn: () =>
      apiGet<{ items: DgDeclaration[] }>(`${SHIPMENTS_API}/${shipmentId}/dg-declarations`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

export function useCreateDgDeclaration(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: DgDeclarationCreate) =>
      apiPost<DgDeclaration>(`${SHIPMENTS_API}/${shipmentId}/dg-declarations`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: dgDeclarationsKey(shipmentId) }),
  });
}

export function useUpdateDgDeclaration(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DgDeclarationUpdate }) =>
      apiPut<DgDeclaration>(`${SHIPMENTS_API}/dg-declarations/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: dgDeclarationsKey(shipmentId) }),
  });
}

export function useDeleteDgDeclaration(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`${SHIPMENTS_API}/dg-declarations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: dgDeclarationsKey(shipmentId) }),
  });
}

export function useSignDgDeclaration(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DgDeclarationSign }) =>
      apiPost<DgDeclaration>(`${SHIPMENTS_API}/dg-declarations/${id}/sign`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: dgDeclarationsKey(shipmentId) }),
  });
}

export function useUnsignDgDeclaration(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<DgDeclaration>(`${SHIPMENTS_API}/dg-declarations/${id}/unsign`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: dgDeclarationsKey(shipmentId) }),
  });
}

export function useCreateDgDeclarationLine(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ declarationId, data }: { declarationId: string; data: DgDeclarationLineCreate }) =>
      apiPost<DgDeclarationLine>(`${SHIPMENTS_API}/dg-declarations/${declarationId}/lines`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: dgDeclarationsKey(shipmentId) }),
  });
}

export function useUpdateDgDeclarationLine(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DgDeclarationLineUpdate }) =>
      apiPut<DgDeclarationLine>(`${SHIPMENTS_API}/dg-declaration-lines/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: dgDeclarationsKey(shipmentId) }),
  });
}

export function useDeleteDgDeclarationLine(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`${SHIPMENTS_API}/dg-declaration-lines/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: dgDeclarationsKey(shipmentId) }),
  });
}

/**
 * Download the branded DGD PDF. Goes through `downloadPdfBlob` (an authenticated XHR),
 * not a bare href — the endpoint requires the bearer token and a VIEWER gate.
 * A draft declaration prints a visible "NOT SIGNED" banner rather than 404ing.
 */
export async function downloadDgDeclarationPdf(
  declarationId: string,
  fileName: string,
): Promise<void> {
  await downloadPdfBlob(`${SHIPMENTS_API}/dg-declarations/${declarationId}/pdf`, fileName);
}

// Free-time-term admin CRUD (Setup → Free-Time Terms). Terms drive the recompute
// service's clock resolution, so a change may alter future clocks.
export function useFreetimeTerms(params?: { charge_type?: string; scope_type?: string; active_only?: boolean }) {
  return useQuery({
    queryKey: ['shipments', 'freetime-terms', params],
    queryFn: () =>
      apiGet<{ items: FreetimeTerm[] }>(`${SHIPMENTS_API}/freetime-terms${buildQueryString(params ?? {})}`),
    select: (data) => data.items,
  });
}

export function useCreateFreetimeTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: FreetimeTermCreate) => apiPost<FreetimeTerm>(`${SHIPMENTS_API}/freetime-terms`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipments', 'freetime-terms'] }),
  });
}

export function useUpdateFreetimeTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: FreetimeTermUpdate }) =>
      apiPut<FreetimeTerm>(`${SHIPMENTS_API}/freetime-terms/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipments', 'freetime-terms'] }),
  });
}

export function useDeleteFreetimeTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`${SHIPMENTS_API}/freetime-terms/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipments', 'freetime-terms'] }),
  });
}

// Holiday-calendar admin (Setup → Holiday Calendar). Closure dates skipped by the
// WORKING day-basis free-time math, so a change may shift future working-basis LFDs.
export function useHolidayCalendars(params?: { active_only?: boolean }) {
  return useQuery({
    queryKey: ['shipments', 'holiday-calendars', params],
    queryFn: () =>
      apiGet<{ items: HolidayCalendar[] }>(`${SHIPMENTS_API}/holiday-calendars${buildQueryString(params ?? {})}`),
    select: (data) => data.items,
  });
}

export function useCreateHolidayCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: HolidayCalendarCreate) =>
      apiPost<HolidayCalendar>(`${SHIPMENTS_API}/holiday-calendars`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipments', 'holiday-calendars'] }),
  });
}

export function useUpdateHolidayCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: HolidayCalendarUpdate }) =>
      apiPut<HolidayCalendar>(`${SHIPMENTS_API}/holiday-calendars/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipments', 'holiday-calendars'] }),
  });
}

export function useDeleteHolidayCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`${SHIPMENTS_API}/holiday-calendars/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipments', 'holiday-calendars'] }),
  });
}

// Warehouse intake-capacity admin (Setup → Warehouse Intake Capacity). The daily
// floor-load ceiling terminal-appointment scheduling enforces per delivery warehouse.
export function useWarehouseIntakeCapacities(params?: { active_only?: boolean }) {
  return useQuery({
    queryKey: ['shipments', 'warehouse-intake-capacities', params],
    queryFn: () =>
      apiGet<{ items: WarehouseIntakeCapacity[] }>(
        `${SHIPMENTS_API}/warehouse-intake-capacities${buildQueryString(params ?? {})}`,
      ),
    select: (data) => data.items,
  });
}

export function useCreateWarehouseIntakeCapacity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: WarehouseIntakeCapacityCreate) =>
      apiPost<WarehouseIntakeCapacity>(`${SHIPMENTS_API}/warehouse-intake-capacities`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipments', 'warehouse-intake-capacities'] }),
  });
}

export function useUpdateWarehouseIntakeCapacity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: WarehouseIntakeCapacityUpdate }) =>
      apiPut<WarehouseIntakeCapacity>(`${SHIPMENTS_API}/warehouse-intake-capacities/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipments', 'warehouse-intake-capacities'] }),
  });
}

export function useDeleteWarehouseIntakeCapacity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`${SHIPMENTS_API}/warehouse-intake-capacities/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shipments', 'warehouse-intake-capacities'] }),
  });
}

// ─── Drayage extras (N7, §6.8) ───
// Terminal appointments and chassis usage hang off a drayage LEG; street turns
// pair two shipments and are read per shipment (both sides of the pairing).

export function useTerminalAppointments(legId: string) {
  return useQuery({
    queryKey: shipmentsKeys.terminalAppointments(legId),
    queryFn: () =>
      apiGet<{ items: TerminalAppointment[] }>(`${SHIPMENTS_API}/legs/${legId}/terminal-appointments`),
    select: (data) => data.items,
    enabled: !!legId,
  });
}

export function useCreateTerminalAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ legId, data }: { legId: string; data: TerminalAppointmentCreate }) =>
      apiPost<TerminalAppointment>(`${SHIPMENTS_API}/legs/${legId}/terminal-appointments`, data),
    onSuccess: (_r, { legId }) =>
      qc.invalidateQueries({ queryKey: shipmentsKeys.terminalAppointments(legId) }),
  });
}

export function useUpdateTerminalAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; legId: string; data: TerminalAppointmentUpdate }) =>
      apiPut<TerminalAppointment>(`${SHIPMENTS_API}/terminal-appointments/${id}`, data),
    onSuccess: (_r, { legId }) =>
      qc.invalidateQueries({ queryKey: shipmentsKeys.terminalAppointments(legId) }),
  });
}

export function useDeleteTerminalAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; legId: string }) =>
      apiDelete(`${SHIPMENTS_API}/terminal-appointments/${id}`),
    onSuccess: (_r, { legId }) =>
      qc.invalidateQueries({ queryKey: shipmentsKeys.terminalAppointments(legId) }),
  });
}

export function useChassisUsage(legId: string) {
  return useQuery({
    queryKey: shipmentsKeys.chassisUsage(legId),
    queryFn: () => apiGet<{ items: ChassisUsage[] }>(`${SHIPMENTS_API}/legs/${legId}/chassis-usage`),
    select: (data) => data.items,
    enabled: !!legId,
  });
}

export function useCreateChassisUsage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ legId, data }: { legId: string; data: ChassisUsageCreate }) =>
      apiPost<ChassisUsage>(`${SHIPMENTS_API}/legs/${legId}/chassis-usage`, data),
    onSuccess: (_r, { legId }) => qc.invalidateQueries({ queryKey: shipmentsKeys.chassisUsage(legId) }),
  });
}

export function useUpdateChassisUsage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; legId: string; data: ChassisUsageUpdate }) =>
      apiPut<ChassisUsage>(`${SHIPMENTS_API}/chassis-usage/${id}`, data),
    onSuccess: (_r, { legId }) => qc.invalidateQueries({ queryKey: shipmentsKeys.chassisUsage(legId) }),
  });
}

export function useDeleteChassisUsage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; legId: string }) =>
      apiDelete(`${SHIPMENTS_API}/chassis-usage/${id}`),
    onSuccess: (_r, { legId }) => qc.invalidateQueries({ queryKey: shipmentsKeys.chassisUsage(legId) }),
  });
}

/** Street turns touching this shipment on either side of the pairing (bounded). */
export function useShipmentStreetTurns(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.shipmentStreetTurns(shipmentId),
    queryFn: () =>
      apiGet<{ items: StreetTurn[]; truncated: boolean }>(
        `${SHIPMENTS_API}/${shipmentId}/street-turns`,
      ),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

// A turn names two files, so a write invalidates every shipment's turn list, not
// just the one it was raised from — the counterparty's panel must refresh too.
function invalidateStreetTurns(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['shipments', 'street-turns'] });
}

export function useCreateStreetTurn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: StreetTurnCreate) => apiPost<StreetTurn>(`${SHIPMENTS_API}/street-turns`, data),
    onSuccess: () => invalidateStreetTurns(qc),
  });
}

export function useUpdateStreetTurn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: StreetTurnUpdate }) =>
      apiPut<StreetTurn>(`${SHIPMENTS_API}/street-turns/${id}`, data),
    onSuccess: () => invalidateStreetTurns(qc),
  });
}

export function useDeleteStreetTurn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`${SHIPMENTS_API}/street-turns/${id}`),
    onSuccess: () => invalidateStreetTurns(qc),
  });
}

// ─── Milestones ───

export function useShipmentMilestones(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.milestones(shipmentId),
    queryFn: () => apiGet<{ items: ShipmentMilestone[] }>(`${SHIPMENTS_API}/${shipmentId}/milestones`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

export function useCreateMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: ShipmentMilestoneCreate }) =>
      apiPost<ShipmentMilestone>(`${SHIPMENTS_API}/${shipmentId}/milestones`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.milestones(shipmentId) }); },
  });
}

export function useDeleteMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, milestoneId }: { shipmentId: string; milestoneId: string }) =>
      apiDelete(`${SHIPMENTS_API}/${shipmentId}/milestones/${milestoneId}`),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.milestones(shipmentId) }); },
  });
}

// ─── Tracking subscriptions ───

export function useTrackingCapabilities() {
  return useQuery({
    queryKey: shipmentsKeys.trackingCapabilities(),
    queryFn: () => apiGet<TrackingCapabilities>(`${SHIPMENTS_API}/tracking/capabilities`),
    staleTime: 5 * 60 * 1000,
  });
}

export function useTrackingSubscriptions(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.trackingSubscriptions(shipmentId),
    queryFn: () => apiGet<{ items: TrackingSubscription[] }>(`${SHIPMENTS_API}/${shipmentId}/tracking`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

export function useSubscribeTracking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: TrackingSubscriptionCreate }) =>
      apiPost<TrackingSubscription>(`${SHIPMENTS_API}/${shipmentId}/tracking`, data),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.trackingSubscriptions(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.milestones(shipmentId) });
    },
  });
}

export function useRefreshTracking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId }: { shipmentId: string; subscriptionId: string }) =>
      apiPost(`${SHIPMENTS_API}/tracking/${subscriptionId}/refresh`, {}),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.trackingSubscriptions(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.milestones(shipmentId) });
    },
  });
}

export function useDeleteTracking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId }: { shipmentId: string; subscriptionId: string }) =>
      apiDelete(`${SHIPMENTS_API}/tracking/${subscriptionId}`),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.trackingSubscriptions(shipmentId) });
    },
  });
}

// ─── Exceptions ───

export function useShipmentExceptions(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.exceptions(shipmentId),
    queryFn: () => apiGet<{ items: ShipmentException[] }>(`${SHIPMENTS_API}/${shipmentId}/exceptions`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

export function useCreateException() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: ShipmentExceptionCreate }) =>
      apiPost<ShipmentException>(`${SHIPMENTS_API}/${shipmentId}/exceptions`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.exceptions(shipmentId) }); },
  });
}

// NOTE: there is deliberately no useDeleteException. An exception is closed
// through its lifecycle (useTransitionException → resolved | dismissed), never
// destroyed: `status` is the module's open/closed discriminator (ERP P1), the
// auto-detectors would re-raise a deleted row on their next pass, and the row
// carries the audit trail a linked cargo claim points at. The module exposes no
// DELETE route for one — a hook that called one 405'd (WI 8d7da7ae).

// ─── Compliance screening (WS5 E3/E4) ───

/** The file's live denied-party screening results + whether it is on a hold. */
export function useShipmentScreening(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.screening(shipmentId),
    queryFn: () => apiGet<ScreeningResponse>(`${SHIPMENTS_API}/${shipmentId}/screening`),
    enabled: !!shipmentId,
  });
}

/** Disposition a screening result on a file (false_positive requires justification). */
export function useDispositionScreening(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: DispositionRequest) =>
      apiPost<ScreeningResult>(`${SHIPMENTS_API}/${shipmentId}/screening/disposition`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.screening(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.exceptions(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.detail(shipmentId) });
    },
  });
}

// ─── Bookings ───

export function useShipmentBookings(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.bookings(shipmentId),
    queryFn: () => apiGet<{ items: Booking[] }>(`${SHIPMENTS_API}/${shipmentId}/bookings`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

export function useCreateBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: BookingCreate }) =>
      apiPost<Booking>(`${SHIPMENTS_API}/${shipmentId}/bookings`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.bookings(shipmentId) }); },
  });
}

export function useUpdateBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, bookingId, data }: { shipmentId: string; bookingId: string; data: BookingUpdate }) =>
      apiPut<Booking>(`${SHIPMENTS_API}/${shipmentId}/bookings/${bookingId}`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.bookings(shipmentId) }); },
  });
}

export function useDeleteBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, bookingId }: { shipmentId: string; bookingId: string }) =>
      apiDelete(`${SHIPMENTS_API}/${shipmentId}/bookings/${bookingId}`),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.bookings(shipmentId) }); },
  });
}

// ─── Booking equipment (the containers a booking covers) ───
// Element-scoped add/edit/remove: an equipment line is a type-and-count request
// ("2 x 40HC") whose optional cargo_id names the container it was fulfilled by.
// Every response reshapes the booking's `equipment` array, so all three
// invalidate the bookings list the tab renders from.

export function useAddBookingEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, bookingId, data }: { shipmentId: string; bookingId: string; data: BookingEquipmentCreate }) =>
      apiPost<BookingEquipment>(`${SHIPMENTS_API}/${shipmentId}/bookings/${bookingId}/equipment`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.bookings(shipmentId) }); },
  });
}

export function useUpdateBookingEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, bookingId, equipmentId, data }: { shipmentId: string; bookingId: string; equipmentId: string; data: BookingEquipmentUpdate }) =>
      apiPut<BookingEquipment>(`${SHIPMENTS_API}/${shipmentId}/bookings/${bookingId}/equipment/${equipmentId}`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.bookings(shipmentId) }); },
  });
}

export function useDeleteBookingEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, bookingId, equipmentId }: { shipmentId: string; bookingId: string; equipmentId: string }) =>
      apiDelete(`${SHIPMENTS_API}/${shipmentId}/bookings/${bookingId}/equipment/${equipmentId}`),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.bookings(shipmentId) }); },
  });
}

// ─── Booking cut-off clocks (SI / VGM / docs) ───

export function useAddBookingCutoff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, bookingId, data }: { shipmentId: string; bookingId: string; data: BookingCutoffCreate }) =>
      apiPost<BookingCutoff>(`${SHIPMENTS_API}/${shipmentId}/bookings/${bookingId}/cutoffs`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.bookings(shipmentId) }); },
  });
}

export function useUpdateBookingCutoff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, bookingId, cutoffId, data }: { shipmentId: string; bookingId: string; cutoffId: string; data: BookingCutoffUpdate }) =>
      apiPatch<BookingCutoff>(`${SHIPMENTS_API}/${shipmentId}/bookings/${bookingId}/cutoffs/${cutoffId}`, data),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.bookings(shipmentId) }); },
  });
}

export function useDeleteBookingCutoff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, bookingId, cutoffId }: { shipmentId: string; bookingId: string; cutoffId: string }) =>
      apiDelete(`${SHIPMENTS_API}/${shipmentId}/bookings/${bookingId}/cutoffs/${cutoffId}`),
    onSuccess: (_r, { shipmentId }) => { qc.invalidateQueries({ queryKey: shipmentsKeys.bookings(shipmentId) }); },
  });
}

// ─── Carrier messages (outbound eBooking / SI / VGM hub) ───

function invalidateCarrierMessages(
  qc: ReturnType<typeof useQueryClient>,
  shipmentId: string,
  bookingId: string
) {
  qc.invalidateQueries({ queryKey: shipmentsKeys.carrierMessages(shipmentId, bookingId) });
  // A confirmed booking request mutates the booking status / carrier booking
  // number, and SI/VGM transmits flip the cut-off clocks shown on the booking card.
  qc.invalidateQueries({ queryKey: shipmentsKeys.bookings(shipmentId) });
}

export function useCarrierMessages(shipmentId: string, bookingId: string) {
  return useQuery({
    queryKey: shipmentsKeys.carrierMessages(shipmentId, bookingId),
    queryFn: () =>
      apiGet<{ items: CarrierMessage[] }>(
        `${SHIPMENTS_API}/${shipmentId}/bookings/${bookingId}/messages`
      ),
    select: (data) => data.items,
    enabled: !!shipmentId && !!bookingId,
  });
}

export function useCarrierMessage(shipmentId: string, bookingId: string, messageId: string | null) {
  return useQuery({
    queryKey: shipmentsKeys.carrierMessage(shipmentId, bookingId, messageId ?? ''),
    queryFn: () =>
      apiGet<CarrierMessageDetail>(
        `${SHIPMENTS_API}/${shipmentId}/bookings/${bookingId}/messages/${messageId}`
      ),
    enabled: !!shipmentId && !!bookingId && !!messageId,
  });
}

export function useCreateCarrierMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, bookingId, messageType }: { shipmentId: string; bookingId: string; messageType: CarrierMessageType }) =>
      apiPost<CarrierMessage>(`${SHIPMENTS_API}/${shipmentId}/bookings/${bookingId}/messages`, {
        message_type: messageType,
      }),
    onSuccess: (_r, { shipmentId, bookingId }) => invalidateCarrierMessages(qc, shipmentId, bookingId),
  });
}

// Mark-ready rejections carry the completeness issue list, which
// BookingCarrierMessagesSection renders in its own validation dialog — so this
// mutation opts out of the generic global mutation-error toast; the section
// toasts the non-validation failures itself.
export function useTransitionCarrierMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      shipmentId,
      bookingId,
      messageId,
      status,
      acknowledgeWarnings,
    }: {
      shipmentId: string;
      bookingId: string;
      messageId: string;
      status: 'draft' | 'ready' | 'amended';
      acknowledgeWarnings?: boolean;
    }) =>
      apiPost<CarrierMessage>(
        `${SHIPMENTS_API}/${shipmentId}/bookings/${bookingId}/messages/${messageId}/transition`,
        { status, acknowledge_warnings: acknowledgeWarnings ?? false }
      ),
    onSuccess: (_r, { shipmentId, bookingId }) => invalidateCarrierMessages(qc, shipmentId, bookingId),
    meta: { suppressGlobalErrorToast: true },
  });
}

export function useTransmitCarrierMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      shipmentId,
      bookingId,
      messageId,
      remarks,
    }: {
      shipmentId: string;
      bookingId: string;
      messageId: string;
      remarks?: string | null;
    }) =>
      apiPost<CarrierMessage>(
        `${SHIPMENTS_API}/${shipmentId}/bookings/${bookingId}/messages/${messageId}/transmit`,
        { remarks: remarks || null }
      ),
    onSuccess: (_r, { shipmentId, bookingId }) => {
      invalidateCarrierMessages(qc, shipmentId, bookingId);
      // Manual transmit attaches the generated SI print / VGM sheet.
      qc.invalidateQueries({ queryKey: shipmentsKeys.documents(shipmentId) });
    },
  });
}

export function useRecordCarrierMessageOutcome() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      shipmentId,
      bookingId,
      messageId,
      outcome,
      carrierReference,
      remarks,
    }: {
      shipmentId: string;
      bookingId: string;
      messageId: string;
      outcome: 'confirmed' | 'declined';
      carrierReference?: string | null;
      remarks?: string | null;
    }) =>
      apiPost<CarrierMessage>(
        `${SHIPMENTS_API}/${shipmentId}/bookings/${bookingId}/messages/${messageId}/outcome`,
        { outcome, carrier_reference: carrierReference || null, remarks: remarks || null }
      ),
    onSuccess: (_r, { shipmentId, bookingId }) => invalidateCarrierMessages(qc, shipmentId, bookingId),
  });
}

export function useDeleteCarrierMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, bookingId, messageId }: { shipmentId: string; bookingId: string; messageId: string }) =>
      apiDelete(`${SHIPMENTS_API}/${shipmentId}/bookings/${bookingId}/messages/${messageId}`),
    onSuccess: (_r, { shipmentId, bookingId }) => invalidateCarrierMessages(qc, shipmentId, bookingId),
  });
}

// ─── Partner requests (Forwarder TMS §5.8) ───

export function usePartnerRequests(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.partnerRequests(shipmentId),
    queryFn: () => apiGet<{ items: PartnerRequest[] }>(`${SHIPMENTS_API}/${shipmentId}/partner-requests`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

export function useCreatePartnerRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: PartnerRequestCreate }) =>
      apiPost<PartnerRequest>(`${SHIPMENTS_API}/${shipmentId}/partner-requests`, data),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.partnerRequests(shipmentId) });
    },
  });
}

export function useUpdatePartnerRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, data }: { shipmentId: string; requestId: string; data: PartnerRequestUpdate }) =>
      apiPut<PartnerRequest>(`${SHIPMENTS_API}/partner-requests/${requestId}`, data),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.partnerRequests(shipmentId) });
    },
  });
}

export function useDeletePartnerRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId }: { shipmentId: string; requestId: string }) =>
      apiDelete(`${SHIPMENTS_API}/partner-requests/${requestId}`),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.partnerRequests(shipmentId) });
    },
  });
}

export function useSendPartnerRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, toEmail }: { shipmentId: string; requestId: string; toEmail?: string | null }) =>
      apiPost<PartnerRequest>(`${SHIPMENTS_API}/partner-requests/${requestId}/send`, { to_email: toEmail ?? null }),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.partnerRequests(shipmentId) });
    },
  });
}

export function useRespondPartnerRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, data }: { shipmentId: string; requestId: string; data: PartnerRequestRespond }) =>
      apiPost<PartnerRequest>(`${SHIPMENTS_API}/partner-requests/${requestId}/respond`, data),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.partnerRequests(shipmentId) });
    },
  });
}

export function useAcceptPartnerRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId }: { shipmentId: string; requestId: string }) =>
      apiPost<PartnerRequest>(`${SHIPMENTS_API}/partner-requests/${requestId}/accept`, {}),
    onSuccess: (_r, { shipmentId }) => {
      // Accept copies quote lines into buy-side charges and can set a leg's
      // responsible org — refresh those surfaces too.
      qc.invalidateQueries({ queryKey: shipmentsKeys.partnerRequests(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.charges(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.legs(shipmentId) });
    },
  });
}

export function useDeclinePartnerRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId }: { shipmentId: string; requestId: string }) =>
      apiPost<PartnerRequest>(`${SHIPMENTS_API}/partner-requests/${requestId}/decline`, {}),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.partnerRequests(shipmentId) });
    },
  });
}

export function useCancelPartnerRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId }: { shipmentId: string; requestId: string }) =>
      apiPost<PartnerRequest>(`${SHIPMENTS_API}/partner-requests/${requestId}/cancel`, {}),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.partnerRequests(shipmentId) });
    },
  });
}

// ─── Freight quote round (OC&CO C4 / gap G12) ───
function useFreightQuoteInvalidate() {
  const qc = useQueryClient();
  return (shipmentId: string) =>
    qc.invalidateQueries({ queryKey: shipmentsKeys.freightQuoteRequests(shipmentId) });
}

export function useFreightQuoteRequests(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.freightQuoteRequests(shipmentId),
    queryFn: () =>
      apiGet<{ items: FreightQuoteRequest[] }>(`${SHIPMENTS_API}/${shipmentId}/freight-quote-requests`),
    select: (data) => data.items,
    enabled: !!shipmentId,
  });
}

export function useCreateFreightQuoteRequest() {
  const invalidate = useFreightQuoteInvalidate();
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: FreightQuoteRequestCreate }) =>
      apiPost<FreightQuoteRequest>(`${SHIPMENTS_API}/${shipmentId}/freight-quote-requests`, data),
    onSuccess: (_r, { shipmentId }) => invalidate(shipmentId),
  });
}

export function useUpdateFreightQuoteRequest() {
  const invalidate = useFreightQuoteInvalidate();
  return useMutation({
    mutationFn: ({ requestId, data }: { shipmentId: string; requestId: string; data: FreightQuoteRequestUpdate }) =>
      apiPut<FreightQuoteRequest>(`${SHIPMENTS_API}/freight-quote-requests/${requestId}`, data),
    onSuccess: (_r, { shipmentId }) => invalidate(shipmentId),
  });
}

export function useDeleteFreightQuoteRequest() {
  const invalidate = useFreightQuoteInvalidate();
  return useMutation({
    mutationFn: ({ requestId }: { shipmentId: string; requestId: string }) =>
      apiDelete(`${SHIPMENTS_API}/freight-quote-requests/${requestId}`),
    onSuccess: (_r, { shipmentId }) => invalidate(shipmentId),
  });
}

export function useAddFreightQuoteRecipient() {
  const invalidate = useFreightQuoteInvalidate();
  return useMutation({
    mutationFn: ({ requestId, data }: { shipmentId: string; requestId: string; data: FreightQuoteRecipientInput }) =>
      apiPost<FreightQuoteRequest>(`${SHIPMENTS_API}/freight-quote-requests/${requestId}/recipients`, data),
    onSuccess: (_r, { shipmentId }) => invalidate(shipmentId),
  });
}

export function useRemoveFreightQuote() {
  const invalidate = useFreightQuoteInvalidate();
  return useMutation({
    mutationFn: ({ quoteId }: { shipmentId: string; quoteId: string }) =>
      apiDelete(`${SHIPMENTS_API}/freight-quotes/${quoteId}`),
    onSuccess: (_r, { shipmentId }) => invalidate(shipmentId),
  });
}

export function useSendFreightQuoteRequest() {
  const invalidate = useFreightQuoteInvalidate();
  return useMutation({
    mutationFn: ({ requestId }: { shipmentId: string; requestId: string }) =>
      apiPost<FreightQuoteRequest>(`${SHIPMENTS_API}/freight-quote-requests/${requestId}/send`, {}),
    onSuccess: (_r, { shipmentId }) => invalidate(shipmentId),
  });
}

export function useRecordFreightQuote() {
  const invalidate = useFreightQuoteInvalidate();
  return useMutation({
    mutationFn: ({ quoteId, data }: { shipmentId: string; quoteId: string; data: FreightQuoteRecord }) =>
      apiPost<FreightQuoteRequest>(`${SHIPMENTS_API}/freight-quotes/${quoteId}/record`, data),
    onSuccess: (_r, { shipmentId }) => invalidate(shipmentId),
  });
}

export function useRequestFreightQuoteApproval() {
  const invalidate = useFreightQuoteInvalidate();
  return useMutation({
    mutationFn: ({ requestId, data }: { shipmentId: string; requestId: string; data: FreightQuoteApprovalRequest }) =>
      apiPost<FreightQuoteRequest>(`${SHIPMENTS_API}/freight-quote-requests/${requestId}/request-approval`, data),
    onSuccess: (_r, { shipmentId }) => invalidate(shipmentId),
  });
}

export function useRecordFreightQuoteDecision() {
  const invalidate = useFreightQuoteInvalidate();
  return useMutation({
    mutationFn: ({ requestId, data }: { shipmentId: string; requestId: string; data: FreightQuoteDecision }) =>
      apiPost<FreightQuoteRequest>(`${SHIPMENTS_API}/freight-quote-requests/${requestId}/decision`, data),
    onSuccess: (_r, { shipmentId }) => invalidate(shipmentId),
  });
}

export function useAwardFreightQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, quoteId }: { shipmentId: string; requestId: string; quoteId?: string }) =>
      apiPost<FreightQuoteRequest>(
        `${SHIPMENTS_API}/freight-quote-requests/${requestId}/award${quoteId ? `?quote_id=${quoteId}` : ''}`,
        {},
      ),
    onSuccess: (_r, { shipmentId }) => {
      // Award stamps a buy-side charge and assigns the winning forwarder as leg
      // vendor — refresh those surfaces too.
      qc.invalidateQueries({ queryKey: shipmentsKeys.freightQuoteRequests(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.charges(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.legs(shipmentId) });
    },
  });
}

export function useCancelFreightQuoteRequest() {
  const invalidate = useFreightQuoteInvalidate();
  return useMutation({
    mutationFn: ({ requestId }: { shipmentId: string; requestId: string }) =>
      apiPost<FreightQuoteRequest>(`${SHIPMENTS_API}/freight-quote-requests/${requestId}/cancel`, {}),
    onSuccess: (_r, { shipmentId }) => invalidate(shipmentId),
  });
}

// ─── Reference Data ───

export function useLoadUnitTypes(params?: ListLoadUnitTypesParams) {
  return useQuery({
    queryKey: shipmentsKeys.loadUnitTypes(params),
    queryFn: () => apiGet<ListResponse<LoadUnitType>>(`${REF_DATA_API}/load-unit-types${buildQueryString(params ?? {})}`),
  });
}

export function usePortsLocations(params?: ListPortsLocationsParams) {
  return useQuery({
    queryKey: shipmentsKeys.portsLocations(params),
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: () => apiGet<ListResponse<PortLocation>>(`${REF_DATA_API}/ports-locations${buildQueryString(params ?? {})}`),
    enabled: !!params,
  });
}

export function usePortLocationFilterFacets() {
  return useQuery({
    queryKey: shipmentsKeys.portLocationFilterFacets(),
    queryFn: ({ signal }) =>
      getPortLocationFilterFacetsApiV1ShipmentsReferenceDataPortsLocationsFilterFacetsGet(
        signal,
      ),
  });
}

export function useAirlines(params?: ListAirlinesParams) {
  return useQuery({
    queryKey: shipmentsKeys.airlines(params),
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: () => apiGet<ListResponse<Airline>>(`${REF_DATA_API}/airlines${buildQueryString(params ?? {})}`),
  });
}

export function useOceanCarriers(params?: ListOceanCarriersParams) {
  return useQuery({
    queryKey: shipmentsKeys.oceanCarriers(params),
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: () => apiGet<ListResponse<OceanCarrier>>(`${REF_DATA_API}/ocean-carriers${buildQueryString(params ?? {})}`),
  });
}

export function useHsCodes(params?: ListHsCodesParams) {
  return useQuery({
    queryKey: shipmentsKeys.hsCodes(params),
    queryFn: () => apiGet<ListResponse<HsCode>>(`${REF_DATA_API}/hs-codes${buildQueryString(params ?? {})}`),
    staleTime: 5 * 60 * 1000, // static reference data
  });
}

// Nomenclatures that actually have codes loaded — drives the page filter so
// only populated schedules (INTL today; US/EU once seeded) are offered.
export function useHsCodeNomenclatures() {
  return useQuery({
    queryKey: shipmentsKeys.hsCodeNomenclatures(),
    queryFn: () => apiGet<{ data: string[] }>(`${REF_DATA_API}/hs-codes/nomenclatures`),
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Routing ───

export function useRoutingEligibility(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.routingEligibility(shipmentId),
    queryFn: () => apiGet<RoutingEligibility>(`${ROUTING_API}/eligibility/${shipmentId}`),
    enabled: !!shipmentId,
  });
}

export function useSuggestPorts(params?: SuggestPortsParams) {
  return useQuery({
    queryKey: shipmentsKeys.routingSuggestPorts(params),
    queryFn: () => apiGet<Record<string, PortSuggestion[]>>(
      `${ROUTING_API}/suggest-ports${buildQueryString(params ?? {})}`
    ),
    enabled: !!params,
  });
}

export function usePlanRoute() {
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: { origin_port_id?: string; destination_port_id?: string } }) =>
      apiPost<RoutingPlanResponse>(`${ROUTING_API}/plan/${shipmentId}`, data),
  });
}

export function useApplyRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: { origin_port_id?: string; destination_port_id?: string } }) =>
      apiPost<RoutingApplyResponse>(`${ROUTING_API}/apply/${shipmentId}`, data),
    onSuccess: (_r, { shipmentId }) => {
      qc.invalidateQueries({ queryKey: shipmentsKeys.legs(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.detail(shipmentId) });
      qc.invalidateQueries({ queryKey: shipmentsKeys.routingEligibility(shipmentId) });
    },
  });
}

// TMS B5 — month-end freight accrual run. Preview is a dry-run (no writes);
// run posts one auto-reversing JE per open shipment with unbilled charges and
// is idempotent per (shipment, period). Both are admin + forwarder-gated.
const FREIGHT_ACCRUALS_API = `${SHIPMENTS_API}/freight-accruals`;

export function usePreviewFreightAccruals() {
  return useMutation({
    mutationFn: (data: FreightAccrualRunRequest) =>
      apiPost<FreightAccrualPlan>(`${FREIGHT_ACCRUALS_API}/preview`, data),
  });
}

export function useRunFreightAccruals() {
  return useMutation({
    mutationFn: (data: FreightAccrualRunRequest) =>
      apiPost<FreightAccrualRunResult>(`${FREIGHT_ACCRUALS_API}/run`, data),
  });
}

// ─── Cargo insurance ────────────────────────────────────────────────────────
// One read serves the whole Coverage tab (coverage block + certificates + premium
// rollup + CIF suggestion). Every write invalidates that one key, and also the
// shipment's exceptions: binding cover clears an open UNINSURED_CARGO exception
// server-side, so the Tracking tab's exception list would otherwise still show it.

export function useShipmentCoverage(shipmentId: string) {
  return useQuery({
    queryKey: shipmentsKeys.coverage(shipmentId),
    queryFn: () =>
      apiGet<ShipmentCoverageDetail>(`${SHIPMENTS_API}/${shipmentId}/coverage`),
    enabled: !!shipmentId,
  });
}

function useCoverageInvalidation() {
  const qc = useQueryClient();
  return (shipmentId: string) => {
    qc.invalidateQueries({ queryKey: shipmentsKeys.coverage(shipmentId) });
    qc.invalidateQueries({ queryKey: shipmentsKeys.exceptions(shipmentId) });
  };
}

export function useUpsertCoverage() {
  const invalidate = useCoverageInvalidation();
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: ShipmentCoverageUpdate }) =>
      apiPut<ShipmentCoverage>(`${SHIPMENTS_API}/${shipmentId}/coverage`, data),
    onSuccess: (_r, { shipmentId }) => invalidate(shipmentId),
  });
}

export function useCreateInsuranceCertificate() {
  const invalidate = useCoverageInvalidation();
  return useMutation({
    mutationFn: ({
      shipmentId,
      data,
    }: {
      shipmentId: string;
      data: InsuranceCertificateCreate;
    }) =>
      apiPost<InsuranceCertificate>(
        `${SHIPMENTS_API}/${shipmentId}/coverage/certificates`,
        data,
      ),
    onSuccess: (_r, { shipmentId }) => invalidate(shipmentId),
  });
}

export function useUpdateInsuranceCertificate() {
  const invalidate = useCoverageInvalidation();
  return useMutation({
    mutationFn: ({
      shipmentId,
      certificateId,
      data,
    }: {
      shipmentId: string;
      certificateId: string;
      data: InsuranceCertificateUpdate;
    }) =>
      apiPut<InsuranceCertificate>(
        `${SHIPMENTS_API}/${shipmentId}/coverage/certificates/${certificateId}`,
        data,
      ),
    onSuccess: (_r, { shipmentId }) => invalidate(shipmentId),
  });
}

export function useDeleteInsuranceCertificate() {
  const invalidate = useCoverageInvalidation();
  return useMutation({
    mutationFn: ({
      shipmentId,
      certificateId,
    }: {
      shipmentId: string;
      certificateId: string;
    }) =>
      apiDelete(`${SHIPMENTS_API}/${shipmentId}/coverage/certificates/${certificateId}`),
    onSuccess: (_r, { shipmentId }) => invalidate(shipmentId),
  });
}
